import React, { useState, useEffect } from 'react';
import { Plus, Eye, FileText, Trash2, Edit2, Printer, Search, GripVertical } from 'lucide-react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '../utils/api';
import { toast } from 'sonner';
import { openPrintWindow } from '../utils/PrintUtils';

// Sortable Line Item Component
const SortableLineItem = ({ 
  id, 
  index, 
  line, 
  serviceItems, 
  serviceSearchTerms, 
  showServiceDropdown,
  setServiceSearchTerms,
  setShowServiceDropdown,
  handleLineChange,
  removeLine 
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1
  };

  // Mobil için card-based layout
  if (isMobile) {
    return (
      <div 
        ref={setNodeRef}
        style={{ 
          ...style,
          background: isDragging ? '#f0f9ff' : 'white',
          padding: '12px',
          borderRadius: '8px',
          border: isDragging ? '2px dashed #3b82f6' : '1px solid #e2e8f0',
          marginBottom: '12px'
        }}
      >
        {/* Başlık satırı - drag handle ve sil butonu */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div 
            {...attributes} 
            {...listeners}
            style={{ cursor: 'grab', color: '#94a3b8', padding: '4px' }}
            title="Sürükleyerek taşı"
          >
            <GripVertical size={20} />
          </div>
          <button type="button" className="btn-icon" onClick={() => removeLine(index)} title="Satırı Sil">
            <Trash2 size={18} color="#ef4444" />
          </button>
        </div>
        
        {/* Hizmet seçimi - tam genişlik */}
        <div style={{ position: 'relative', marginBottom: '10px' }}>
          <label style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '4px', display: 'block' }}>Hizmet</label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Hizmet ara..."
              value={serviceSearchTerms[index] !== undefined ? serviceSearchTerms[index] : line.description}
              onChange={(e) => {
                setServiceSearchTerms(prev => ({ ...prev, [index]: e.target.value }));
                setShowServiceDropdown(prev => ({ ...prev, [index]: true }));
              }}
              onFocus={() => {
                setShowServiceDropdown(prev => ({ ...prev, [index]: true }));
                if (serviceSearchTerms[index] === undefined) {
                  setServiceSearchTerms(prev => ({ ...prev, [index]: '' }));
                }
              }}
              onBlur={() => {
                setTimeout(() => {
                  setShowServiceDropdown(prev => ({ ...prev, [index]: false }));
                  if (serviceSearchTerms[index] === '') {
                    setServiceSearchTerms(prev => ({ ...prev, [index]: undefined }));
                  }
                }, 200);
              }}
              data-testid={`line-desc-${index}`}
              style={{ fontSize: '0.85rem', paddingRight: '30px', width: '100%' }}
            />
            <Search size={14} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          </div>
          {showServiceDropdown[index] && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              maxHeight: '200px',
              overflowY: 'auto',
              zIndex: 1000
            }}>
              {[
                ...(line.description && !serviceItems.find(s => (typeof s === 'string' ? s : s.name) === line.description) 
                  ? [{ name: line.description, isCustom: true }] 
                  : []),
                ...serviceItems.map(s => ({ name: typeof s === 'string' ? s : s.name, original: s })),
                { name: 'Diğer / Other', isDiger: true }
              ]
                .filter(item => {
                  const searchTerm = (serviceSearchTerms[index] || '').toLowerCase();
                  return item.name.toLowerCase().includes(searchTerm);
                })
                .slice(0, 12)
                .map((item, idx) => (
                  <div
                    key={idx}
                    onClick={() => {
                      handleLineChange(index, 'description', item.name);
                      setServiceSearchTerms(prev => ({ ...prev, [index]: undefined }));
                      setShowServiceDropdown(prev => ({ ...prev, [index]: false }));
                    }}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f1f5f9',
                      fontSize: '0.85rem',
                      background: item.isCustom ? '#fef3c7' : item.isDiger ? '#f0fdf4' : 'white'
                    }}
                  >
                    {item.name}
                  </div>
                ))}
            </div>
          )}
        </div>
        
        {/* İki sütunlu alan - Adet ve Birim */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
          <div>
            <label style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '4px', display: 'block' }}>Adet</label>
            <input
              type="number"
              className="form-input"
              value={line.quantity}
              onChange={(e) => handleLineChange(index, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
              min="1"
              required
              data-testid={`line-qty-${index}`}
              style={{ fontSize: '0.85rem', width: '100%' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '4px', display: 'block' }}>Birim</label>
            <select
              className="form-select"
              value={line.unit || 'ADET'}
              onChange={(e) => handleLineChange(index, 'unit', e.target.value)}
              style={{ fontSize: '0.85rem', width: '100%' }}
            >
              <option value="ADET">Adet</option>
              <option value="SAAT">Saat</option>
              <option value="GUN">Gün</option>
              <option value="SEFER">Sefer</option>
              <option value="TON">Ton</option>
              <option value="M3">M³</option>
            </select>
          </div>
        </div>
        
        {/* İki sütunlu alan - Birim Fiyat ve KDV */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
          <div>
            <label style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '4px', display: 'block' }}>Birim Fiyat</label>
            <input
              type="number"
              className="form-input"
              value={line.unit_price}
              onChange={(e) => handleLineChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
              step="0.001"
              min="0"
              required
              data-testid={`line-price-${index}`}
              style={{ fontSize: '0.85rem', width: '100%' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '4px', display: 'block' }}>KDV</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={line.has_vat || false}
                onChange={(e) => handleLineChange(index, 'has_vat', e.target.checked)}
                style={{ width: '18px', height: '18px' }}
              />
              {line.has_vat && (
                <input
                  type="number"
                  className="form-input"
                  value={line.vat_rate || 20}
                  onChange={(e) => handleLineChange(index, 'vat_rate', parseInt(e.target.value) || 0)}
                  min="0"
                  max="100"
                  style={{ fontSize: '0.85rem', width: '60px' }}
                />
              )}
              {line.has_vat && <span style={{ fontSize: '0.75rem' }}>%</span>}
            </div>
          </div>
        </div>
        
        {/* Toplam - altına */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: '1px solid #e2e8f0' }}>
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Toplam:</span>
          <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1e40af' }}>
            {((line.quantity || 1) * (line.unit_price || 0) * (1 + (line.has_vat ? (line.vat_rate || 20) / 100 : 0))).toFixed(2)}
          </span>
        </div>
      </div>
    );
  }

  // Desktop için mevcut grid layout
  return (
    <div 
      ref={setNodeRef}
      style={{ 
        ...style,
        display: 'grid', 
        gridTemplateColumns: '30px 2fr 70px 70px 90px 50px 70px 90px 50px', 
        gap: '0.5rem', 
        marginBottom: '0.5rem',
        background: isDragging ? '#f0f9ff' : 'white',
        padding: '4px',
        borderRadius: '6px',
        border: isDragging ? '2px dashed #3b82f6' : '1px solid transparent'
      }}
    >
      {/* Drag Handle */}
      <div 
        {...attributes} 
        {...listeners}
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          cursor: 'grab',
          color: '#94a3b8',
          padding: '4px'
        }}
        title="Sürükleyerek taşı"
      >
        <GripVertical size={18} />
      </div>
      
      {/* Aranabilir Hizmet Seçimi */}
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            className="form-input"
            placeholder="Hizmet ara..."
            value={serviceSearchTerms[index] !== undefined ? serviceSearchTerms[index] : line.description}
            onChange={(e) => {
              setServiceSearchTerms(prev => ({ ...prev, [index]: e.target.value }));
              setShowServiceDropdown(prev => ({ ...prev, [index]: true }));
            }}
            onFocus={() => {
              setShowServiceDropdown(prev => ({ ...prev, [index]: true }));
              if (serviceSearchTerms[index] === undefined) {
                setServiceSearchTerms(prev => ({ ...prev, [index]: '' }));
              }
            }}
            onBlur={() => {
              setTimeout(() => {
                setShowServiceDropdown(prev => ({ ...prev, [index]: false }));
                if (serviceSearchTerms[index] === '') {
                  setServiceSearchTerms(prev => ({ ...prev, [index]: undefined }));
                }
              }, 200);
            }}
            data-testid={`line-desc-${index}`}
            style={{ fontSize: '0.75rem', paddingRight: '24px' }}
          />
          <Search size={12} style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
        </div>
        {showServiceDropdown[index] && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            maxHeight: '180px',
            overflowY: 'auto',
            zIndex: 1000
          }}>
            {[
              ...(line.description && !serviceItems.find(s => (typeof s === 'string' ? s : s.name) === line.description) 
                ? [{ name: line.description, isCustom: true }] 
                : []),
              ...serviceItems.map(s => ({ name: typeof s === 'string' ? s : s.name, original: s })),
              { name: 'Diğer / Other', isDiger: true }
            ]
              .filter(item => {
                const searchTerm = (serviceSearchTerms[index] || '').toLowerCase();
                return item.name.toLowerCase().includes(searchTerm);
              })
              .slice(0, 12)
              .map((item, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    handleLineChange(index, 'description', item.name);
                    setServiceSearchTerms(prev => ({ ...prev, [index]: undefined }));
                    setShowServiceDropdown(prev => ({ ...prev, [index]: false }));
                  }}
                  style={{
                    padding: '6px 10px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f1f5f9',
                    fontSize: '0.75rem',
                    background: item.isCustom ? '#fef3c7' : item.isDiger ? '#f0fdf4' : 'white'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.target.style.background = item.isCustom ? '#fef3c7' : item.isDiger ? '#f0fdf4' : 'white'}
                >
                  {item.name}
                </div>
              ))}
          </div>
        )}
      </div>
      <input
        type="number"
        className="form-input"
        placeholder="Adet"
        value={line.quantity}
        onChange={(e) => handleLineChange(index, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
        min="1"
        required
        data-testid={`line-qty-${index}`}
        style={{ fontSize: '0.75rem' }}
      />
      <select
        className="form-select"
        value={line.unit || 'ADET'}
        onChange={(e) => handleLineChange(index, 'unit', e.target.value)}
        style={{ fontSize: '0.75rem' }}
      >
        <option value="ADET">Adet</option>
        <option value="SAAT">Saat</option>
        <option value="GUN">Gün</option>
        <option value="SEFER">Sefer</option>
        <option value="TON">Ton</option>
        <option value="M3">M³</option>
      </select>
      <input
        type="number"
        className="form-input"
        placeholder="Fiyat"
        value={line.unit_price}
        onChange={(e) => handleLineChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
        step="0.001"
        min="0"
        required
        data-testid={`line-price-${index}`}
        style={{ fontSize: '0.75rem' }}
      />
      <input
        type="checkbox"
        checked={line.has_vat || false}
        onChange={(e) => handleLineChange(index, 'has_vat', e.target.checked)}
        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
        data-testid={`line-vat-${index}`}
      />
      <select
        className="form-select"
        value={line.vat_rate || 20}
        onChange={(e) => handleLineChange(index, 'vat_rate', parseFloat(e.target.value) || 0)}
        disabled={!line.has_vat}
        style={{ fontSize: '0.75rem', opacity: line.has_vat ? 1 : 0.5 }}
      >
        <option value={20}>%20</option>
        <option value={18}>%18</option>
        <option value={10}>%10</option>
        <option value={8}>%8</option>
        <option value={1}>%1</option>
      </select>
      <input
        type="text"
        className="form-input"
        value={(() => {
          const baseTotal = line.quantity * line.unit_price;
          const vatAmount = line.has_vat ? baseTotal * (line.vat_rate || 20) / 100 : 0;
          return (baseTotal + vatAmount).toFixed(2);
        })()}
        readOnly
        style={{ background: '#f1f5f9', fontSize: '0.75rem', fontWeight: '500' }}
        data-testid={`line-total-${index}`}
      />
      <button 
        type="button" 
        className="action-btn action-btn-danger" 
        onClick={() => removeLine(index)}
        data-testid={`remove-line-${index}`}
        title="Sil"
        style={{ padding: '4px 8px' }}
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
};

const Proformas = () => {
  const [proformas, setProformas] = useState([]);
  const [clients, setClients] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [serviceItems, setServiceItems] = useState([]);
  const [exchangeRates, setExchangeRates] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedProforma, setSelectedProforma] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [vesselSearchTerm, setVesselSearchTerm] = useState(null);
  const [showVesselDropdown, setShowVesselDropdown] = useState(false);
  const [portCallTypes, setPortCallTypes] = useState([]);
  const [ports, setPorts] = useState([]); // Liman listesi
  const [portSearchTerm, setPortSearchTerm] = useState('');
  const [showPortDropdown, setShowPortDropdown] = useState(false);
  const [serviceSearchTerms, setServiceSearchTerms] = useState({}); // Her satır için ayrı arama terimi
  const [showServiceDropdown, setShowServiceDropdown] = useState({}); // Her satır için dropdown durumu
  const [formData, setFormData] = useState({
    client_id: '',
    vessel_name: '',
    port: '',  // Liman seçimi
    port_code: '', // Liman kodu
    operation_type: '',
    status: 'draft',
    currency: 'USD',
    notes: '',
    lines: [{ description: '', quantity: 1, unit: 'ADET', unit_price: 0, has_vat: false, vat_rate: 20, total: 0 }]
  });
  const [portCalls, setPortCalls] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [proformasRes, clientsRes, vesselsRes, ratesRes, settingsRes, portCallsRes] = await Promise.all([
        api.get('/proformas'),
        api.get('/clients'),
        api.get('/vessels'),
        api.get('/exchange-rates/current'),
        api.get('/settings'),
        api.get('/port-calls')
      ]);
      setProformas(proformasRes.data);
      setClients(clientsRes.data);
      setVessels(vesselsRes.data);
      setExchangeRates(ratesRes.data);
      setSettings(settingsRes.data);
      setPortCalls(portCallsRes.data);
      
      // Hizmet kalemlerini settings'den al
      if (settingsRes.data && settingsRes.data.service_items) {
        setServiceItems(settingsRes.data.service_items);
      }
      // Liman çağrısı tiplerini settings'den al
      if (settingsRes.data && settingsRes.data.port_call_types) {
        setPortCallTypes(settingsRes.data.port_call_types.filter(t => t.active !== false));
      }
      // Limanları settings'den al
      if (settingsRes.data && settingsRes.data.ports) {
        setPorts(settingsRes.data.ports);
      }
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleLineChange = async (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    
    // Hizmet seçildiğinde otomatik fiyat getir ve KDV ayarlarını uygula
    if (field === 'description' && value && value !== 'Diğer / Other') {
      // Önce service_items'dan varsayılan fiyat ve KDV ayarlarını kontrol et
      const selectedService = serviceItems.find(item => 
        (typeof item === 'string' ? item : item.name) === value
      );
      
      if (selectedService && typeof selectedService === 'object') {
        // Varsayılan fiyatı otomatik uygula (eğer tanımlıysa ve para birimi uyuşuyorsa)
        if (selectedService.default_price && selectedService.default_price > 0) {
          const serviceCurrency = selectedService.currency || 'USD';
          
          if (serviceCurrency === formData.currency) {
            // Para birimi aynıysa direkt uygula
            newLines[index].unit_price = selectedService.default_price;
            newLines[index].total = newLines[index].quantity * selectedService.default_price;
            toast.info(`Varsayılan fiyat uygulandı: ${selectedService.default_price} ${serviceCurrency}`);
          } else if (exchangeRates && exchangeRates.currencies) {
            // Para birimi farklıysa dönüştür
            let convertedPrice = selectedService.default_price;
            
            // Önce TRY'ye çevir, sonra hedef para birimine
            if (serviceCurrency === 'USD' && exchangeRates.currencies.USD) {
              const tryAmount = selectedService.default_price * exchangeRates.currencies.USD.forex_buying;
              if (formData.currency === 'TRY') {
                convertedPrice = tryAmount;
              } else if (formData.currency === 'EUR' && exchangeRates.currencies.EUR) {
                convertedPrice = tryAmount / exchangeRates.currencies.EUR.forex_buying;
              }
            } else if (serviceCurrency === 'EUR' && exchangeRates.currencies.EUR) {
              const tryAmount = selectedService.default_price * exchangeRates.currencies.EUR.forex_buying;
              if (formData.currency === 'TRY') {
                convertedPrice = tryAmount;
              } else if (formData.currency === 'USD' && exchangeRates.currencies.USD) {
                convertedPrice = tryAmount / exchangeRates.currencies.USD.forex_buying;
              }
            } else if (serviceCurrency === 'TRY') {
              if (formData.currency === 'USD' && exchangeRates.currencies.USD) {
                convertedPrice = selectedService.default_price / exchangeRates.currencies.USD.forex_buying;
              } else if (formData.currency === 'EUR' && exchangeRates.currencies.EUR) {
                convertedPrice = selectedService.default_price / exchangeRates.currencies.EUR.forex_buying;
              }
            }
            
            newLines[index].unit_price = parseFloat(convertedPrice.toFixed(2));
            newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
            toast.info(`Fiyat dönüştürüldü: ${selectedService.default_price} ${serviceCurrency} → ${convertedPrice.toFixed(2)} ${formData.currency}`);
          }
        }
        
        // KDV ayarlarını otomatik uygula
        if (selectedService.vat_enabled !== undefined) {
          newLines[index].has_vat = selectedService.vat_enabled;
          newLines[index].vat_rate = selectedService.vat_rate || 20;
          if (selectedService.vat_enabled) {
            toast.info(`KDV otomatik uygulandı: %${selectedService.vat_rate || 20}`);
          }
        }
      }
      
      // Mesai Yolluk Ücreti için özel hesaplama
      if (value === 'Mesai Yolluk Ücreti / Custom Over Time' && exchangeRates && exchangeRates.currencies && settings) {
        const basePriceTRY = settings.overtime_fee_try || 4650; // Ayarlardan al veya varsayılan
        let calculatedPrice = 0;
        
        if (formData.currency === 'USD' && exchangeRates.currencies.USD) {
          calculatedPrice = basePriceTRY / exchangeRates.currencies.USD.forex_buying;
          toast.info(`Mesai yolluk ücreti: ${basePriceTRY} TRY ÷ ${exchangeRates.currencies.USD.forex_buying.toFixed(4)} = ${calculatedPrice.toFixed(2)} USD`);
        } else if (formData.currency === 'EUR' && exchangeRates.currencies.EUR) {
          calculatedPrice = basePriceTRY / exchangeRates.currencies.EUR.forex_buying;
          toast.info(`Mesai yolluk ücreti: ${basePriceTRY} TRY ÷ ${exchangeRates.currencies.EUR.forex_buying.toFixed(4)} = ${calculatedPrice.toFixed(2)} EUR`);
        } else if (formData.currency === 'TRY') {
          calculatedPrice = basePriceTRY;
          toast.info(`Mesai yolluk ücreti: ${basePriceTRY} TRY`);
        } else {
          // GBP veya diğer para birimleri için varsayılan
          calculatedPrice = 0;
        }
        
        newLines[index].unit_price = parseFloat(calculatedPrice.toFixed(2));
        newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
      } 
      // HSSGM Tahakkuku için özel hesaplama
      else if (value === 'HSSGM Tahakuku / Sanitary Dues' && exchangeRates && exchangeRates.currencies && settings && formData.vessel_name) {
        // Seçili geminin NRT'sini bul
        const selectedVessel = vessels.find(v => v.name === formData.vessel_name);
        
        if (selectedVessel && selectedVessel.net_tonnage) {
          // Varsayılan olarak ticari gemi tarifesi kullan
          const tariffTRY = settings.hssgm_commercial || 17.27;
          const nrt = parseFloat(selectedVessel.net_tonnage);
          const totalTRY = nrt * tariffTRY;
          
          let calculatedPrice = 0;
          
          if (formData.currency === 'USD' && exchangeRates.currencies.USD) {
            calculatedPrice = totalTRY / exchangeRates.currencies.USD.forex_buying;
            toast.info(`HSSGM: ${nrt} NRT × ${tariffTRY} TL = ${totalTRY.toFixed(2)} TRY ÷ ${exchangeRates.currencies.USD.forex_buying.toFixed(4)} = ${calculatedPrice.toFixed(2)} USD`);
          } else if (formData.currency === 'EUR' && exchangeRates.currencies.EUR) {
            calculatedPrice = totalTRY / exchangeRates.currencies.EUR.forex_buying;
            toast.info(`HSSGM: ${nrt} NRT × ${tariffTRY} TL = ${totalTRY.toFixed(2)} TRY ÷ ${exchangeRates.currencies.EUR.forex_buying.toFixed(4)} = ${calculatedPrice.toFixed(2)} EUR`);
          } else if (formData.currency === 'TRY') {
            calculatedPrice = totalTRY;
            toast.info(`HSSGM: ${nrt} NRT × ${tariffTRY} TL = ${totalTRY.toFixed(2)} TRY`);
          } else {
            calculatedPrice = 0;
          }
          
          newLines[index].unit_price = parseFloat(calculatedPrice.toFixed(2));
          newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
        } else {
          toast.warning('Gemi NRT (Net Tonnage) bilgisi bulunamadı. Lütfen gemiye NRT değeri ekleyin.');
        }
      }
      // Kıyı Emniyeti Fener Ücreti için özel hesaplama
      else if (value === 'Kıyı Emniyeti Fener Ücretti / Light Dues' && settings && formData.vessel_name) {
        const selectedVessel = vessels.find(v => v.name === formData.vessel_name);
        
        if (selectedVessel && selectedVessel.net_tonnage && selectedVessel.flag) {
          const nrt = parseFloat(selectedVessel.net_tonnage);
          const flag = selectedVessel.flag.toLowerCase();
          
          // Tarifeyi seç (Türk bayraklı mı değil mi)
          let rate0_800 = 0;
          let rate800_plus = 0;
          let flagType = '';
          
          if (flag.includes('turk') || flag.includes('turkey')) {
            // Türk bayraklı - varsayılan olarak "Kabotaj Hariç" kullan
            rate0_800 = settings.light_dues_turkish_0_800 || 0.1241856;
            rate800_plus = settings.light_dues_turkish_800_plus || 0.0620928;
            flagType = 'Türk Bayraklı';
          } else {
            // Yabancı bayraklı - varsayılan olarak "Ticaret" kullan
            rate0_800 = settings.light_dues_foreign_commercial_0_800 || 0.22176;
            rate800_plus = settings.light_dues_foreign_commercial_800_plus || 0.11088;
            flagType = 'Yabancı Bayraklı';
          }
          
          // Hesaplama: İlk 800 ton için bir tarife, geri kalanı için başka tarife
          let singleDirectionUSD = 0;
          if (nrt <= 800) {
            singleDirectionUSD = nrt * rate0_800;
          } else {
            singleDirectionUSD = (800 * rate0_800) + ((nrt - 800) * rate800_plus);
          }
          
          // Liman Giriş + Liman Çıkış = × 2
          const totalUSD = singleDirectionUSD * 2;
          
          newLines[index].unit_price = parseFloat(totalUSD.toFixed(2));
          newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
          
          toast.info(`Kıyı Emniyeti Fener (${flagType}): ${nrt} NT × 2 (Giriş+Çıkış) = $${totalUSD.toFixed(2)} USD`);
        } else {
          toast.warning('Gemi NRT ve bayrak bilgisi gerekli. Lütfen gemi bilgilerini kontrol edin.');
        }
      }
      // DTO Liman Hizmet Ücreti için özel hesaplama
      else if ((value === 'DTO Liman hizmet Ücreti/ DTO Port Service Fee' || value === 'DTO Liman hizmet Ücreti / DTO Port Service Fee') && settings && formData.vessel_name && exchangeRates && exchangeRates.currencies) {
        const selectedVessel = vessels.find(v => v.name === formData.vessel_name);
        
        if (selectedVessel && selectedVessel.flag) {
          const flag = selectedVessel.flag.toLowerCase();
          let feeTRY = 0;
          let flagType = '';
          
          if (flag.includes('turk') || flag.includes('turkey')) {
            feeTRY = settings.dto_port_service_turkish || 2800.0;
            flagType = 'Türk Bayraklı';
          } else {
            feeTRY = settings.dto_port_service_foreign || 4900.0;
            flagType = 'Yabancı Bayraklı';
          }
          
          // TRY'den seçili para birimine çevir
          let calculatedPrice = 0;
          
          if (formData.currency === 'USD' && exchangeRates.currencies.USD) {
            calculatedPrice = feeTRY / exchangeRates.currencies.USD.forex_buying;
            toast.info(`DTO Liman Hizmet (${flagType}): ${feeTRY} TRY ÷ ${exchangeRates.currencies.USD.forex_buying.toFixed(4)} = $${calculatedPrice.toFixed(2)} USD`);
          } else if (formData.currency === 'EUR' && exchangeRates.currencies.EUR) {
            calculatedPrice = feeTRY / exchangeRates.currencies.EUR.forex_buying;
            toast.info(`DTO Liman Hizmet (${flagType}): ${feeTRY} TRY ÷ ${exchangeRates.currencies.EUR.forex_buying.toFixed(4)} = €${calculatedPrice.toFixed(2)} EUR`);
          } else if (formData.currency === 'TRY') {
            calculatedPrice = feeTRY;
            toast.info(`DTO Liman Hizmet (${flagType}): ${feeTRY} TRY`);
          } else {
            calculatedPrice = 0;
          }
          
          newLines[index].unit_price = parseFloat(calculatedPrice.toFixed(2));
          newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
        } else {
          toast.warning('Gemi bayrak bilgisi gerekli. Lütfen gemi bilgilerini kontrol edin.');
        }
      }
      // DTO Navlun Hasılatı Oda Payı için özel hesaplama
      else if (value === 'DTO Navlun Hasılatı Oda Payı / CSFFFR' && settings) {
        // Varsayılan olarak konteyner tek yön kullan
        const feeUSD = settings.dto_freight_container_single || 700.0;
        
        // USD'den seçili para birimine çevir (gerekirse)
        let calculatedPrice = 0;
        
        if (formData.currency === 'USD') {
          calculatedPrice = feeUSD;
          toast.info(`DTO Navlun Hasılatı (Konteyner): $${feeUSD} USD`);
        } else if (formData.currency === 'EUR' && exchangeRates && exchangeRates.currencies && exchangeRates.currencies.EUR && exchangeRates.currencies.USD) {
          // USD'den EUR'ya çevir (USD/EUR paritesi)
          const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
          calculatedPrice = feeUSD / usdToEurRate;
          toast.info(`DTO Navlun Hasılatı: $${feeUSD} USD = €${calculatedPrice.toFixed(2)} EUR`);
        } else if (formData.currency === 'TRY' && exchangeRates && exchangeRates.currencies && exchangeRates.currencies.USD) {
          calculatedPrice = feeUSD * exchangeRates.currencies.USD.forex_buying;
          toast.info(`DTO Navlun Hasılatı: $${feeUSD} USD × ${exchangeRates.currencies.USD.forex_buying.toFixed(4)} = ${calculatedPrice.toFixed(2)} TRY`);
        } else {
          calculatedPrice = feeUSD;
        }
        
        newLines[index].unit_price = parseFloat(calculatedPrice.toFixed(2));
        newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
      }
      // LÇB Fazla Mesai Ücreti için özel hesaplama (Liman Başkanlığı)
      else if (settings?.port_authority_lcb_overtime_service_name && value === settings.port_authority_lcb_overtime_service_name && formData.vessel_name) {
        const selectedVessel = vessels.find(v => v.name === formData.vessel_name);
        
        if (selectedVessel && selectedVessel.net_tonnage) {
          try {
            // API'den LÇB Fazla Mesai ücretini hesapla
            const calcResponse = await api.post(`/port-authority/calculate-lcb-overtime?net_tonnage=${parseFloat(selectedVessel.net_tonnage)}`);
            
            if (calcResponse.data && calcResponse.data.lcb_overtime_usd) {
              let calculatedPrice = calcResponse.data.lcb_overtime_usd;
              
              // Para birimi dönüşümü (USD'den)
              if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR && exchangeRates?.currencies?.USD) {
                const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
                calculatedPrice = calcResponse.data.lcb_overtime_usd / usdToEurRate;
              } else if (formData.currency === 'TRY') {
                calculatedPrice = calcResponse.data.lcb_overtime_try;
              }
              
              newLines[index].unit_price = parseFloat(calculatedPrice.toFixed(2));
              newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
              
              toast.success(
                <div>
                  <strong>LÇB Fazla Mesai Hesaplandı</strong>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem' }}>
                    {selectedVessel.net_tonnage} NT → {calcResponse.data.tier_description}<br/>
                    {calcResponse.data.lcb_overtime_try.toFixed(2)} TL → {formData.currency === 'USD' ? '$' : formData.currency === 'EUR' ? '€' : '₺'}{calculatedPrice.toFixed(2)} {formData.currency}
                  </p>
                </div>,
                { duration: 5000 }
              );
            }
          } catch (error) {
            console.error('LÇB Fazla Mesai calculation error:', error);
            toast.warning('LÇB Fazla Mesai hesaplanamadı. Tarife ayarlarını kontrol edin.');
          }
        } else {
          toast.warning('LÇB Fazla Mesai için geminin Net Tonaj (NT) bilgisi gerekli.');
        }
      }
      // Ordino Fazla Mesai Ücreti için özel hesaplama (LÇB / 2)
      else if (settings?.port_authority_ordino_overtime_service_name && value === settings.port_authority_ordino_overtime_service_name && formData.vessel_name) {
        const selectedVessel = vessels.find(v => v.name === formData.vessel_name);
        
        if (selectedVessel && selectedVessel.net_tonnage) {
          try {
            // API'den Ordino Fazla Mesai ücretini hesapla (LÇB'nin yarısı)
            const calcResponse = await api.post(`/port-authority/calculate-lcb-overtime?net_tonnage=${parseFloat(selectedVessel.net_tonnage)}`);
            
            if (calcResponse.data && calcResponse.data.ordino_overtime_usd) {
              let calculatedPrice = calcResponse.data.ordino_overtime_usd;
              
              // Para birimi dönüşümü (USD'den)
              if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR && exchangeRates?.currencies?.USD) {
                const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
                calculatedPrice = calcResponse.data.ordino_overtime_usd / usdToEurRate;
              } else if (formData.currency === 'TRY') {
                calculatedPrice = calcResponse.data.ordino_overtime_try;
              }
              
              newLines[index].unit_price = parseFloat(calculatedPrice.toFixed(2));
              newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
              
              toast.success(
                <div>
                  <strong>Ordino Fazla Mesai Hesaplandı</strong>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem' }}>
                    {selectedVessel.net_tonnage} NT → {calcResponse.data.tier_description}<br/>
                    {calcResponse.data.ordino_overtime_try.toFixed(2)} TL (LÇB/2) → {formData.currency === 'USD' ? '$' : formData.currency === 'EUR' ? '€' : '₺'}{calculatedPrice.toFixed(2)} {formData.currency}
                  </p>
                </div>,
                { duration: 5000 }
              );
            }
          } catch (error) {
            console.error('Ordino Fazla Mesai calculation error:', error);
            toast.warning('Ordino Fazla Mesai hesaplanamadı. Tarife ayarlarını kontrol edin.');
          }
        } else {
          toast.warning('Ordino Fazla Mesai için geminin Net Tonaj (NT) bilgisi gerekli.');
        }
      }
      // LÇB C Kodu Ücreti için özel hesaplama (Liman Başkanlığı)
      else if (settings?.port_authority_lcb_c_code_service_name && value === settings.port_authority_lcb_c_code_service_name && formData.vessel_name) {
        const selectedVessel = vessels.find(v => v.name === formData.vessel_name);
        
        if (selectedVessel && selectedVessel.net_tonnage) {
          try {
            // API'den LÇB C Kodu ücretini hesapla
            const calcResponse = await api.post(`/port-authority/calculate-lcb-c-code?net_tonnage=${parseFloat(selectedVessel.net_tonnage)}`);
            
            if (calcResponse.data && calcResponse.data.lcb_c_code_usd) {
              let calculatedPrice = calcResponse.data.lcb_c_code_usd;
              
              // Para birimi dönüşümü (USD'den)
              if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR && exchangeRates?.currencies?.USD) {
                const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
                calculatedPrice = calcResponse.data.lcb_c_code_usd / usdToEurRate;
              } else if (formData.currency === 'TRY') {
                calculatedPrice = calcResponse.data.lcb_c_code_try;
              }
              
              newLines[index].unit_price = parseFloat(calculatedPrice.toFixed(2));
              newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
              
              toast.success(
                <div>
                  <strong>LÇB C Kodu Hesaplandı</strong>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem' }}>
                    {selectedVessel.net_tonnage} NT → {calcResponse.data.tier_description}<br/>
                    {calcResponse.data.lcb_c_code_try.toFixed(2)} TL → {formData.currency === 'USD' ? '$' : formData.currency === 'EUR' ? '€' : '₺'}{calculatedPrice.toFixed(2)} {formData.currency}
                  </p>
                </div>,
                { duration: 5000 }
              );
            }
          } catch (error) {
            console.error('LÇB C Kodu calculation error:', error);
            toast.warning('LÇB C Kodu hesaplanamadı. Tarife ayarlarını kontrol edin.');
          }
        } else {
          toast.warning('LÇB C Kodu için geminin Net Tonaj (NT) bilgisi gerekli.');
        }
      }
      // Demirleme Ücreti için özel hesaplama (Liman Başkanlığı - GT bazlı)
      else if (settings?.port_authority_anchoring_service_name && value === settings.port_authority_anchoring_service_name && formData.vessel_name) {
        const selectedVessel = vessels.find(v => v.name === formData.vessel_name);
        
        if (selectedVessel && selectedVessel.gross_tonnage) {
          try {
            // Bayrak kontrolü
            const flag = (selectedVessel.flag || '').toLowerCase();
            const isTurkishFlag = flag.includes('turk') || flag.includes('turkey');
            
            // API'den Demirleme ücretini hesapla (varsayılan 1 gün)
            const calcResponse = await api.post(`/port-authority/calculate-anchoring?gross_tonnage=${parseFloat(selectedVessel.gross_tonnage)}&days=1&is_turkish_flag=${isTurkishFlag}`);
            
            if (calcResponse.data && calcResponse.data.total_fee_usd) {
              let calculatedPrice = calcResponse.data.total_fee_usd;
              
              // Para birimi dönüşümü (USD'den)
              if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR && exchangeRates?.currencies?.USD) {
                const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
                calculatedPrice = calcResponse.data.total_fee_usd / usdToEurRate;
              } else if (formData.currency === 'TRY') {
                calculatedPrice = calcResponse.data.total_fee_try;
              }
              
              newLines[index].unit_price = parseFloat(calculatedPrice.toFixed(2));
              newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
              
              toast.success(
                <div>
                  <strong>Demirleme Ücreti Hesaplandı</strong>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem' }}>
                    {calcResponse.data.flag_text} - {selectedVessel.gross_tonnage} GT<br/>
                    Günlük: ${calcResponse.data.daily_fee_usd} → {formData.currency === 'USD' ? '$' : formData.currency === 'EUR' ? '€' : '₺'}{calculatedPrice.toFixed(2)} {formData.currency}
                  </p>
                </div>,
                { duration: 5000 }
              );
            }
          } catch (error) {
            console.error('Demirleme ücreti calculation error:', error);
            toast.warning('Demirleme ücreti hesaplanamadı. Tarife ayarlarını kontrol edin.');
          }
        } else {
          toast.warning('Demirleme ücreti için geminin Gross Tonaj (GT) bilgisi gerekli.');
        }
      }
      // Acentelik Ücreti için otomatik tarife hesaplama
      else if (settings?.agency_fee_service_id && value && formData.vessel_name) {
        // Eşleştirilmiş hizmet kalemini kontrol et
        const selectedService = serviceItems.find(item => 
          (typeof item === 'string' ? item : item.name) === value
        );
        const serviceId = selectedService?.id;
        
        // Bu hizmet kalemi acentelik tarifesiyle eşleştirilmiş mi?
        if (serviceId === settings.agency_fee_service_id || 
            (settings.agency_fee_service_name && value.includes(settings.agency_fee_service_name))) {
          // Gemiyi bul ve NT'yi al
          const selectedVessel = vessels.find(v => v.name === formData.vessel_name);
          
          if (selectedVessel && selectedVessel.net_tonnage) {
            try {
              // API'den acentelik ücretini hesapla
              const calcResponse = await api.post('/agency-tariffs/calculate', {
                net_tonnage: parseFloat(selectedVessel.net_tonnage),
                stay_days: 1  // Varsayılan 1 gün
              });
              
              if (calcResponse.data && calcResponse.data.total_fee_usd) {
                let calculatedPrice = calcResponse.data.total_fee_usd;
                
                // Para birimi dönüşümü (USD'den)
                if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR && exchangeRates?.currencies?.USD) {
                  const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
                  calculatedPrice = calcResponse.data.total_fee_usd / usdToEurRate;
                } else if (formData.currency === 'TRY' && exchangeRates?.currencies?.USD) {
                  calculatedPrice = calcResponse.data.total_fee_usd * exchangeRates.currencies.USD.forex_buying;
                }
                
                newLines[index].unit_price = parseFloat(calculatedPrice.toFixed(2));
                newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
                
                toast.success(
                  <div>
                    <strong>Acentelik Ücreti Hesaplandı</strong>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem' }}>
                      {selectedVessel.net_tonnage} NT → {calcResponse.data.tier_description}<br/>
                      €{calcResponse.data.total_fee_eur.toFixed(2)} → {formData.currency === 'USD' ? '$' : formData.currency === 'EUR' ? '€' : ''}{calculatedPrice.toFixed(2)} {formData.currency}
                    </p>
                  </div>,
                  { duration: 5000 }
                );
              }
            } catch (error) {
              console.error('Agency tariff calculation error:', error);
              toast.warning('Acentelik tarifesi hesaplanamadı. Tarife ayarlarını kontrol edin.');
            }
          } else {
            toast.warning('Acentelik ücreti için geminin Net Tonaj (NT) bilgisi gerekli.');
          }
        }
      }
      // Kıyı Emniyeti (Fener Ücreti) için otomatik hesaplama
      else if (settings?.light_dues_service_name && value === settings.light_dues_service_name && formData.vessel_name) {
        const selectedVessel = vessels.find(v => v.name === formData.vessel_name);
        
        if (selectedVessel && selectedVessel.net_tonnage) {
          const nt = parseFloat(selectedVessel.net_tonnage);
          const flag = (selectedVessel.flag || '').toLowerCase();
          const isTurkish = flag.includes('turk') || flag.includes('turkey') || flag.includes('türk');
          
          // Oran seçimi (NT bazlı, bayrak bazlı)
          let rate;
          if (isTurkish) {
            rate = nt <= 800 ? (settings.light_dues_turkish_0_800 || 0.1241856) : (settings.light_dues_turkish_800_plus || 0.0620928);
          } else {
            rate = nt <= 800 ? (settings.light_dues_foreign_commercial_0_800 || 0.22176) : (settings.light_dues_foreign_commercial_800_plus || 0.11088);
          }
          
          // Hesaplama: NT × Oran (USD)
          let calculatedPrice = nt * rate;
          
          // Para birimi dönüşümü
          if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR && exchangeRates?.currencies?.USD) {
            const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
            calculatedPrice = calculatedPrice / usdToEurRate;
          } else if (formData.currency === 'TRY' && exchangeRates?.currencies?.USD) {
            calculatedPrice = calculatedPrice * exchangeRates.currencies.USD.forex_buying;
          }
          
          newLines[index].unit_price = parseFloat(calculatedPrice.toFixed(2));
          newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
          
          toast.success(
            <div>
              <strong>Kıyı Emniyeti Hesaplandı</strong>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem' }}>
                {nt} NT × {rate} $/NT = ${(nt * rate).toFixed(2)}<br/>
                {isTurkish ? 'Türk Bayraklı' : 'Yabancı Bayraklı'}
              </p>
            </div>,
            { duration: 5000 }
          );
        } else {
          toast.warning('Kıyı Emniyeti için geminin Net Tonaj (NT) bilgisi gerekli.');
        }
      }
      // HSSGM (Sağlık Resimleri) için otomatik hesaplama
      else if (settings?.hssgm_service_name && value === settings.hssgm_service_name && formData.vessel_name) {
        const selectedVessel = vessels.find(v => v.name === formData.vessel_name);
        
        if (selectedVessel && selectedVessel.net_tonnage) {
          const nrt = parseFloat(selectedVessel.net_tonnage);
          
          // Varsayılan: Ticari gemi tarifesi (TL/NRT)
          const hssgmRate = settings.hssgm_commercial || 17.27;
          let calculatedPriceTRY = nrt * hssgmRate;
          let calculatedPrice = calculatedPriceTRY;
          
          // Para birimi dönüşümü (TRY'den)
          if (formData.currency === 'USD' && exchangeRates?.currencies?.USD) {
            calculatedPrice = calculatedPriceTRY / exchangeRates.currencies.USD.forex_buying;
          } else if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR) {
            calculatedPrice = calculatedPriceTRY / exchangeRates.currencies.EUR.forex_buying;
          }
          
          newLines[index].unit_price = parseFloat(calculatedPrice.toFixed(2));
          newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
          
          toast.success(
            <div>
              <strong>HSSGM Hesaplandı</strong>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem' }}>
                {nrt} NRT × {hssgmRate} TL/NRT = {calculatedPriceTRY.toFixed(2)} TL
              </p>
            </div>,
            { duration: 5000 }
          );
        } else {
          toast.warning('HSSGM için geminin Net Tonaj (NRT) bilgisi gerekli.');
        }
      }
      // DTO (Deniz Ticaret Odası) için otomatik hesaplama (GT bazlı kademeli)
      else if (settings?.dto_service_name && value === settings.dto_service_name && formData.vessel_name) {
        const selectedVessel = vessels.find(v => v.name === formData.vessel_name);
        
        if (selectedVessel && selectedVessel.gross_tonnage) {
          const flag = (selectedVessel.flag || '').toLowerCase();
          const isTurkish = flag.includes('turk') || flag.includes('turkey') || flag.includes('türk');
          const vesselType = (selectedVessel.type || '').toLowerCase();
          const isPassenger = vesselType.includes('passenger') || vesselType.includes('cruise') || vesselType.includes('yolcu') || vesselType.includes('kruvaziyer');
          
          try {
            // API'den DTO ücretini hesapla
            const calcResponse = await api.post(`/dto/calculate?gross_tonnage=${parseFloat(selectedVessel.gross_tonnage)}&is_turkish_flag=${isTurkish}&is_passenger=${isPassenger}`);
            
            if (calcResponse.data && calcResponse.data.dto_fee_try) {
              let calculatedPrice = calcResponse.data.dto_fee_try;
              
              // Para birimi dönüşümü (TRY'den)
              if (formData.currency === 'USD') {
                calculatedPrice = calcResponse.data.dto_fee_usd;
              } else if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR && exchangeRates?.currencies?.USD) {
                const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
                calculatedPrice = calcResponse.data.dto_fee_usd / usdToEurRate;
              }
              
              newLines[index].unit_price = parseFloat(calculatedPrice.toFixed(2));
              newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
              
              toast.success(
                <div>
                  <strong>DTO Ücreti Hesaplandı</strong>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem' }}>
                    {calcResponse.data.flag_text} - {calcResponse.data.ship_type}<br/>
                    {selectedVessel.gross_tonnage} GT → {calcResponse.data.tier_description}<br/>
                    {calcResponse.data.dto_fee_try.toLocaleString('tr-TR')} TL
                  </p>
                </div>,
                { duration: 5000 }
              );
            }
          } catch (error) {
            console.error('DTO calculation error:', error);
            toast.warning('DTO ücreti hesaplanamadı. Tarife ayarlarını kontrol edin.');
          }
        } else {
          toast.warning('DTO ücreti için geminin Gross Tonaj (GT) bilgisi gerekli.');
        }
      }
      // DTO Navlun Oda Payı için otomatik hesaplama
      else if (settings?.dto_freight_service_name && value === settings.dto_freight_service_name) {
        // Varsayılan tekli konteyner navlun ücreti (USD)
        const freightFeeUSD = settings.dto_freight_container_single || 700.0;
        let calculatedPrice = freightFeeUSD;
        
        // Para birimi dönüşümü (USD'den)
        if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR && exchangeRates?.currencies?.USD) {
          const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
          calculatedPrice = freightFeeUSD / usdToEurRate;
        } else if (formData.currency === 'TRY' && exchangeRates?.currencies?.USD) {
          calculatedPrice = freightFeeUSD * exchangeRates.currencies.USD.forex_buying;
        }
        
        newLines[index].unit_price = parseFloat(calculatedPrice.toFixed(2));
        newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
        
        toast.success(
          <div>
            <strong>DTO Navlun Oda Payı Hesaplandı</strong>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem' }}>
              Navlun Hasılatı: ${freightFeeUSD.toFixed(2)} USD
            </p>
          </div>,
          { duration: 5000 }
        );
      }
      // Mesai Yolluk için otomatik hesaplama
      else if (settings?.overtime_service_name && value === settings.overtime_service_name) {
        const overtimeFeeTRY = settings.overtime_fee_try || 7510.0;
        let calculatedPrice = overtimeFeeTRY;
        
        // Para birimi dönüşümü (TRY'den)
        if (formData.currency === 'USD' && exchangeRates?.currencies?.USD) {
          calculatedPrice = overtimeFeeTRY / exchangeRates.currencies.USD.forex_buying;
        } else if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR) {
          calculatedPrice = overtimeFeeTRY / exchangeRates.currencies.EUR.forex_buying;
        }
        
        newLines[index].unit_price = parseFloat(calculatedPrice.toFixed(2));
        newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
        
        toast.success(
          <div>
            <strong>Mesai Yolluk Hesaplandı</strong>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem' }}>
              Sabit Ücret: {overtimeFeeTRY.toLocaleString('tr-TR')} TL
            </p>
          </div>,
          { duration: 5000 }
        );
      }
      // Transit Vize Harcı için otomatik hesaplama
      else if (settings?.transit_visa_service_name && value === settings.transit_visa_service_name) {
        const transitVisaFeeTRY = settings.transit_visa_fee_try || 9376.40;
        let calculatedPrice = transitVisaFeeTRY;
        
        // Para birimi dönüşümü (TRY'den)
        if (formData.currency === 'USD' && exchangeRates?.currencies?.USD) {
          calculatedPrice = transitVisaFeeTRY / exchangeRates.currencies.USD.forex_buying;
        } else if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR) {
          calculatedPrice = transitVisaFeeTRY / exchangeRates.currencies.EUR.forex_buying;
        }
        
        newLines[index].unit_price = parseFloat(calculatedPrice.toFixed(2));
        newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
        
        toast.success(
          <div>
            <strong>Transit Vize Harcı Hesaplandı</strong>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem' }}>
              Sabit Ücret: {transitVisaFeeTRY.toLocaleString('tr-TR')} TL
            </p>
          </div>,
          { duration: 5000 }
        );
      } else {
        // Diğer hizmetler için normal fiyat çekme
        try {
          const response = await api.get('/service-prices/last-price', {
            params: {
              service_name: value,
              client_id: formData.client_id || null
            }
          });
          
          if (response.data && response.data.unit_price) {
            newLines[index].unit_price = response.data.unit_price;
            newLines[index].total = newLines[index].quantity * response.data.unit_price;
            
            // Bilgilendirme toast'ı göster
            if (response.data.from_client) {
              toast.info(`Bu müşteri için son fiyat: ${response.data.unit_price} ${formData.currency}`);
            } else {
              toast.info(`Genel son fiyat: ${response.data.unit_price} ${formData.currency}`);
            }
          }
        } catch (error) {
          // Hata durumunda sessizce devam et, kullanıcı manuel girebilir
          console.log('Son fiyat bulunamadı, manuel giriş yapılabilir');
        }
      }
    }
    
    if (field === 'quantity' || field === 'unit_price' || field === 'has_vat' || field === 'vat_rate') {
      const baseTotal = newLines[index].quantity * newLines[index].unit_price;
      const vatAmount = newLines[index].has_vat ? baseTotal * (newLines[index].vat_rate || 20) / 100 : 0;
      newLines[index].total = baseTotal + vatAmount;
    }
    
    setFormData({ ...formData, lines: newLines });
  };

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { description: '', quantity: 1, unit: 'ADET', unit_price: 0, has_vat: false, vat_rate: 20, total: 0 }]
    });
  };

  const removeLine = (index) => {
    const newLines = formData.lines.filter((_, i) => i !== index);
    if (newLines.length === 0) {
      newLines.push({ description: '', quantity: 1, unit: 'ADET', unit_price: 0, has_vat: false, vat_rate: 20, total: 0 });
    }
    setFormData({ ...formData, lines: newLines });
  };

  // Kalem sırasını yukarı taşı
  const moveLineUp = (index) => {
    if (index === 0) return;
    const newLines = [...formData.lines];
    [newLines[index - 1], newLines[index]] = [newLines[index], newLines[index - 1]];
    setFormData({ ...formData, lines: newLines });
  };

  // Kalem sırasını aşağı taşı
  const moveLineDown = (index) => {
    if (index === formData.lines.length - 1) return;
    const newLines = [...formData.lines];
    [newLines[index], newLines[index + 1]] = [newLines[index + 1], newLines[index]];
    setFormData({ ...formData, lines: newLines });
  };

  const handleOperationTypeChange = async (operationType) => {
    // Operasyon tipine göre otomatik hizmet kalemleri ekle
    if (operationType && serviceItems && serviceItems.length > 0) {
      // Get selected vessel for price calculation
      // Try exact match first, then partial match
      let selectedVessel = formData.vessel_name ? vessels.find(v => v.name === formData.vessel_name) : null;
      if (!selectedVessel && formData.vessel_name) {
        // Partial match fallback
        selectedVessel = vessels.find(v => v.name.toLowerCase().includes(formData.vessel_name.toLowerCase()));
      }
      let servicesToAdd = [];
      
      // Operasyon tipine göre filtrele
      // "transit" için auto_add_transit, "ship_inspection" için auto_add_inspection, diğerleri için auto_add_other
      if (operationType === 'transit') {
        servicesToAdd = serviceItems.filter(item => {
          if (typeof item === 'string') return false;
          return item.auto_add_transit === true;
        });
      } else if (operationType === 'ship_inspection') {
        // Gemi Kontrol
        servicesToAdd = serviceItems.filter(item => {
          if (typeof item === 'string') return false;
          return item.auto_add_inspection === true;
        });
      } else if (operationType && operationType !== 'other') {
        // Dinamik tipler için - hem inspection hem transit kontrol et
        servicesToAdd = serviceItems.filter(item => {
          if (typeof item === 'string') return false;
          return item.auto_add_inspection === true || item.auto_add_transit === true;
        });
      } else if (operationType === 'other') {
        servicesToAdd = serviceItems.filter(item => {
          if (typeof item === 'string') return false;
          return item.auto_add_other === true;
        });
      }
      
      // NOT: Tarife eşleştirmelerindeki hizmetler SADECE hizmet kalemlerinde
      // auto_add_* bayrağı seçili ise eklenir. Bu kontrol zaten yukarıda yapılıyor.
      // Tarife eşleştirmeleri sadece FİYAT HESAPLAMASI için kullanılır.
      
      if (servicesToAdd.length > 0) {
        // Her hizmet için fiyat hesapla
        const newLines = [];
        for (const serviceItem of servicesToAdd) {
          const serviceName = serviceItem.name;
          let calculatedPrice = 0;
          let priceCalculated = false;
          
          // Mesai Yolluk Ücreti için özel hesaplama
          if (serviceName === 'Mesai Yolluk Ücreti / Custom Over Time' && exchangeRates && exchangeRates.currencies && settings) {
            const basePriceTRY = settings.overtime_fee_try || 4650;
            
            if (formData.currency === 'USD' && exchangeRates.currencies.USD) {
              calculatedPrice = basePriceTRY / exchangeRates.currencies.USD.forex_buying;
            } else if (formData.currency === 'EUR' && exchangeRates.currencies.EUR) {
              calculatedPrice = basePriceTRY / exchangeRates.currencies.EUR.forex_buying;
            } else if (formData.currency === 'TRY') {
              calculatedPrice = basePriceTRY;
            }
            priceCalculated = true;
          }
          // HSSGM Tahakkuku için özel hesaplama
          else if (serviceName === 'HSSGM Tahakuku / Sanitary Dues' && exchangeRates && exchangeRates.currencies && settings && selectedVessel) {
            if (selectedVessel.net_tonnage) {
              const tariffTRY = settings.hssgm_commercial || 17.27;
              const nrt = parseFloat(selectedVessel.net_tonnage);
              const totalTRY = nrt * tariffTRY;
              
              if (formData.currency === 'USD' && exchangeRates.currencies.USD) {
                calculatedPrice = totalTRY / exchangeRates.currencies.USD.forex_buying;
              } else if (formData.currency === 'EUR' && exchangeRates.currencies.EUR) {
                calculatedPrice = totalTRY / exchangeRates.currencies.EUR.forex_buying;
              } else if (formData.currency === 'TRY') {
                calculatedPrice = totalTRY;
              }
              priceCalculated = true;
            }
          }
          // Kıyı Emniyeti Fener Ücreti için özel hesaplama
          else if ((serviceName === 'Kıyı Emniyeti Fener Ücreti / Light Dues' || serviceName === 'Kıyı Emniyeti Fener Ücretti / Light Dues') && settings && selectedVessel) {
            if (selectedVessel.net_tonnage && selectedVessel.flag) {
              const nrt = parseFloat(selectedVessel.net_tonnage);
              const flag = selectedVessel.flag.toLowerCase();
              
              let rate0_800 = 0;
              let rate800_plus = 0;
              
              if (flag.includes('turk') || flag.includes('turkey')) {
                rate0_800 = settings.light_dues_turkish_0_800 || 0.1241856;
                rate800_plus = settings.light_dues_turkish_800_plus || 0.0620928;
              } else {
                rate0_800 = settings.light_dues_foreign_commercial_0_800 || 0.22176;
                rate800_plus = settings.light_dues_foreign_commercial_800_plus || 0.11088;
              }
              
              let singleDirectionUSD = 0;
              if (nrt <= 800) {
                singleDirectionUSD = nrt * rate0_800;
              } else {
                singleDirectionUSD = (800 * rate0_800) + ((nrt - 800) * rate800_plus);
              }
              
              calculatedPrice = singleDirectionUSD * 2; // Giriş + Çıkış
              priceCalculated = true;
            }
          }
          // DTO Liman Hizmet Ücreti için özel hesaplama
          else if ((serviceName === 'DTO Liman hizmet Ücreti/ DTO Port Service Fee' || serviceName === 'DTO Liman hizmet Ücreti / DTO Port Service Fee') && settings && selectedVessel && exchangeRates && exchangeRates.currencies) {
            if (selectedVessel.flag) {
              const flag = selectedVessel.flag.toLowerCase();
              let feeTRY = 0;
              
              if (flag.includes('turk') || flag.includes('turkey')) {
                feeTRY = settings.dto_port_service_turkish || 2800.0;
              } else {
                feeTRY = settings.dto_port_service_foreign || 4900.0;
              }
              
              if (formData.currency === 'USD' && exchangeRates.currencies.USD) {
                calculatedPrice = feeTRY / exchangeRates.currencies.USD.forex_buying;
              } else if (formData.currency === 'EUR' && exchangeRates.currencies.EUR) {
                calculatedPrice = feeTRY / exchangeRates.currencies.EUR.forex_buying;
              } else if (formData.currency === 'TRY') {
                calculatedPrice = feeTRY;
              }
              priceCalculated = true;
            }
          }
          // DTO Navlun Hasılatı için özel hesaplama
          else if (serviceName === 'DTO Navlun Hasılatı Oda Payı / CSFFFR' && settings) {
            const feeUSD = settings.dto_freight_container_single || 700.0;
            
            if (formData.currency === 'USD') {
              calculatedPrice = feeUSD;
            } else if (formData.currency === 'EUR' && exchangeRates && exchangeRates.currencies && exchangeRates.currencies.EUR && exchangeRates.currencies.USD) {
              const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
              calculatedPrice = feeUSD / usdToEurRate;
            } else if (formData.currency === 'TRY' && exchangeRates && exchangeRates.currencies && exchangeRates.currencies.USD) {
              calculatedPrice = feeUSD * exchangeRates.currencies.USD.forex_buying;
            } else {
              calculatedPrice = feeUSD;
            }
            priceCalculated = true;
          }
          // Kıyı Emniyeti (Fener Ücreti) için özel hesaplama
          else if (settings?.light_dues_service_name && serviceName === settings.light_dues_service_name) {
            if (selectedVessel && selectedVessel.net_tonnage) {
              const nt = parseFloat(selectedVessel.net_tonnage);
              const flag = (selectedVessel.flag || '').toLowerCase();
              const isTurkish = flag.includes('turk') || flag.includes('turkey') || flag.includes('türk');
              
              let rate;
              if (isTurkish) {
                rate = nt <= 800 ? (settings.light_dues_turkish_0_800 || 0.1241856) : (settings.light_dues_turkish_800_plus || 0.0620928);
              } else {
                rate = nt <= 800 ? (settings.light_dues_foreign_commercial_0_800 || 0.22176) : (settings.light_dues_foreign_commercial_800_plus || 0.11088);
              }
              
              let lightDuesUSD = nt * rate;
              
              if (formData.currency === 'USD') {
                calculatedPrice = lightDuesUSD;
              } else if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR && exchangeRates?.currencies?.USD) {
                const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
                calculatedPrice = lightDuesUSD / usdToEurRate;
              } else if (formData.currency === 'TRY' && exchangeRates?.currencies?.USD) {
                calculatedPrice = lightDuesUSD * exchangeRates.currencies.USD.forex_buying;
              }
              priceCalculated = true;
            } else {
              const defaultPrice = serviceItem.default_price || 0;
              if (defaultPrice > 0) {
                calculatedPrice = defaultPrice;
                priceCalculated = true;
              } else {
                toast.warning(`Fener Ücreti için gemi Net Tonaj (NT) bilgisi gerekli. Lütfen fiyatı manuel girin.`);
              }
            }
          }
          // HSSGM (Sağlık Resimleri) için özel hesaplama
          else if (settings?.hssgm_service_name && serviceName === settings.hssgm_service_name) {
            if (selectedVessel && selectedVessel.net_tonnage) {
              const nrt = parseFloat(selectedVessel.net_tonnage);
              const hssgmRate = settings.hssgm_commercial || 17.27;
              let hssgmTRY = nrt * hssgmRate;
              
              if (formData.currency === 'TRY') {
                calculatedPrice = hssgmTRY;
              } else if (formData.currency === 'USD' && exchangeRates?.currencies?.USD) {
                calculatedPrice = hssgmTRY / exchangeRates.currencies.USD.forex_buying;
              } else if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR) {
                calculatedPrice = hssgmTRY / exchangeRates.currencies.EUR.forex_buying;
              }
              priceCalculated = true;
            } else {
              const defaultPrice = serviceItem.default_price || 0;
              if (defaultPrice > 0) {
                calculatedPrice = defaultPrice;
                priceCalculated = true;
              } else {
                toast.warning(`HSSGM için gemi Net Tonaj (NT) bilgisi gerekli. Lütfen fiyatı manuel girin.`);
              }
            }
          }
          // DTO Hizmet Eşleştirmesi için özel hesaplama (GT bazlı kademeli)
          else if (settings?.dto_service_name && serviceName === settings.dto_service_name) {
            if (selectedVessel && selectedVessel.gross_tonnage) {
              try {
                const flag = (selectedVessel.flag || '').toLowerCase();
                const isTurkish = flag.includes('turk') || flag.includes('turkey') || flag.includes('türk');
                const vesselType = (selectedVessel.type || '').toLowerCase();
                const isPassenger = vesselType.includes('passenger') || vesselType.includes('cruise') || vesselType.includes('yolcu') || vesselType.includes('kruvaziyer');
                
                const calcResponse = await api.post(`/dto/calculate?gross_tonnage=${parseFloat(selectedVessel.gross_tonnage)}&is_turkish_flag=${isTurkish}&is_passenger=${isPassenger}`);
                
                if (calcResponse.data) {
                  if (formData.currency === 'TRY') {
                    calculatedPrice = calcResponse.data.dto_fee_try;
                  } else if (formData.currency === 'USD') {
                    calculatedPrice = calcResponse.data.dto_fee_usd;
                  } else if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR && exchangeRates?.currencies?.USD) {
                    const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
                    calculatedPrice = calcResponse.data.dto_fee_usd / usdToEurRate;
                  }
                  priceCalculated = true;
                }
              } catch (error) {
                console.error('DTO auto-calc error:', error);
              }
            } else {
              const defaultPrice = serviceItem.default_price || 0;
              if (defaultPrice > 0) {
                calculatedPrice = defaultPrice;
                priceCalculated = true;
              } else {
                toast.warning(`DTO için gemi Gross Tonaj (GT) bilgisi gerekli. Lütfen fiyatı manuel girin.`);
              }
            }
          }
          // DTO Navlun Oda Payı için özel hesaplama
          else if (settings?.dto_freight_service_name && serviceName === settings.dto_freight_service_name) {
            const freightFeeUSD = settings.dto_freight_container_single || 700.0;
            
            if (formData.currency === 'USD') {
              calculatedPrice = freightFeeUSD;
            } else if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR && exchangeRates?.currencies?.USD) {
              const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
              calculatedPrice = freightFeeUSD / usdToEurRate;
            } else if (formData.currency === 'TRY' && exchangeRates?.currencies?.USD) {
              calculatedPrice = freightFeeUSD * exchangeRates.currencies.USD.forex_buying;
            }
            priceCalculated = true;
          }
          // Mesai Yolluk için özel hesaplama
          else if (settings?.overtime_service_name && serviceName === settings.overtime_service_name) {
            const overtimeFeeTRY = settings.overtime_fee_try || 7510.0;
            
            if (formData.currency === 'TRY') {
              calculatedPrice = overtimeFeeTRY;
            } else if (formData.currency === 'USD' && exchangeRates?.currencies?.USD) {
              calculatedPrice = overtimeFeeTRY / exchangeRates.currencies.USD.forex_buying;
            } else if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR) {
              calculatedPrice = overtimeFeeTRY / exchangeRates.currencies.EUR.forex_buying;
            }
            priceCalculated = true;
          }
          // Transit Vize Harcı için özel hesaplama
          else if (settings?.transit_visa_service_name && serviceName === settings.transit_visa_service_name) {
            const transitVisaFeeTRY = settings.transit_visa_fee_try || 9376.40;
            
            if (formData.currency === 'TRY') {
              calculatedPrice = transitVisaFeeTRY;
            } else if (formData.currency === 'USD' && exchangeRates?.currencies?.USD) {
              calculatedPrice = transitVisaFeeTRY / exchangeRates.currencies.USD.forex_buying;
            } else if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR) {
              calculatedPrice = transitVisaFeeTRY / exchangeRates.currencies.EUR.forex_buying;
            }
            priceCalculated = true;
          }
          // Acentelik Ücreti (Agency Fee) için özel hesaplama - NRT bazlı kademeli
          else if (settings?.agency_fee_service_name && serviceName === settings.agency_fee_service_name) {
            if (selectedVessel && selectedVessel.net_tonnage) {
              try {
                const calcResponse = await api.post('/agency-tariffs/calculate', {
                  net_tonnage: parseFloat(selectedVessel.net_tonnage),
                  stay_days: 1
                });
                
                if (calcResponse.data) {
                  if (formData.currency === 'USD') {
                    calculatedPrice = calcResponse.data.total_fee_usd;
                  } else if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR && exchangeRates?.currencies?.USD) {
                    const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
                    calculatedPrice = calcResponse.data.total_fee_usd / usdToEurRate;
                  } else if (formData.currency === 'TRY' && exchangeRates?.currencies?.USD) {
                    calculatedPrice = calcResponse.data.total_fee_usd * exchangeRates.currencies.USD.forex_buying;
                  } else {
                    calculatedPrice = calcResponse.data.total_fee_usd;
                  }
                  priceCalculated = true;
                }
              } catch (error) {
                console.error('Acentelik Ücreti auto-calc error:', error);
              }
            } else {
              const defaultPrice = serviceItem.default_price || 0;
              if (defaultPrice > 0) {
                calculatedPrice = defaultPrice;
                priceCalculated = true;
              } else {
                toast.warning(`Acentelik Ücreti için gemi Net Tonaj (NT) bilgisi gerekli. Lütfen fiyatı manuel girin.`);
              }
            }
          }
          // LÇB Fazla Mesai Ücreti için özel hesaplama (Liman Başkanlığı)
          else if (settings?.port_authority_lcb_overtime_service_name && serviceName === settings.port_authority_lcb_overtime_service_name) {
            if (selectedVessel && selectedVessel.net_tonnage) {
              try {
                const calcResponse = await api.post(`/port-authority/calculate-lcb-overtime?net_tonnage=${parseFloat(selectedVessel.net_tonnage)}`);
                
                if (calcResponse.data) {
                  if (formData.currency === 'USD') {
                    calculatedPrice = calcResponse.data.lcb_overtime_usd;
                  } else if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR && exchangeRates?.currencies?.USD) {
                    const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
                    calculatedPrice = calcResponse.data.lcb_overtime_usd / usdToEurRate;
                  } else if (formData.currency === 'TRY') {
                    calculatedPrice = calcResponse.data.lcb_overtime_try;
                  }
                  priceCalculated = true;
                }
              } catch (error) {
                console.error('LÇB Fazla Mesai auto-calc error:', error);
              }
            } else {
              // Gemi seçilmemiş veya NRT yok - varsayılan fiyatı kullan veya uyarı ver
              const defaultPrice = serviceItem.default_price || 0;
              if (defaultPrice > 0) {
                calculatedPrice = defaultPrice;
                priceCalculated = true;
              } else {
                toast.warning(`LÇB Fazla Mesai için gemi Net Tonaj (NT) bilgisi gerekli. Lütfen fiyatı manuel girin.`);
              }
            }
          }
          // Ordino Fazla Mesai Ücreti için özel hesaplama (LÇB / 2)
          else if (settings?.port_authority_ordino_overtime_service_name && serviceName === settings.port_authority_ordino_overtime_service_name) {
            if (selectedVessel && selectedVessel.net_tonnage) {
              try {
                const calcResponse = await api.post(`/port-authority/calculate-lcb-overtime?net_tonnage=${parseFloat(selectedVessel.net_tonnage)}`);
                
                if (calcResponse.data) {
                  if (formData.currency === 'USD') {
                    calculatedPrice = calcResponse.data.ordino_overtime_usd;
                  } else if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR && exchangeRates?.currencies?.USD) {
                    const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
                    calculatedPrice = calcResponse.data.ordino_overtime_usd / usdToEurRate;
                  } else if (formData.currency === 'TRY') {
                    calculatedPrice = calcResponse.data.ordino_overtime_try;
                  }
                  priceCalculated = true;
                }
              } catch (error) {
                console.error('Ordino Fazla Mesai auto-calc error:', error);
              }
            } else {
              // Gemi seçilmemiş veya NRT yok - varsayılan fiyatı kullan veya uyarı ver
              const defaultPrice = serviceItem.default_price || 0;
              if (defaultPrice > 0) {
                calculatedPrice = defaultPrice;
                priceCalculated = true;
              } else {
                toast.warning(`Ordino Fazla Mesai için gemi Net Tonaj (NT) bilgisi gerekli. Lütfen fiyatı manuel girin.`);
              }
            }
          }
          // LÇB C Kodu Ücreti için özel hesaplama (Liman Başkanlığı)
          else if (settings?.port_authority_lcb_c_code_service_name && serviceName === settings.port_authority_lcb_c_code_service_name) {
            if (selectedVessel && selectedVessel.net_tonnage) {
              try {
                const calcResponse = await api.post(`/port-authority/calculate-lcb-c-code?net_tonnage=${parseFloat(selectedVessel.net_tonnage)}`);
                
                if (calcResponse.data) {
                  if (formData.currency === 'USD') {
                    calculatedPrice = calcResponse.data.lcb_c_code_usd;
                  } else if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR && exchangeRates?.currencies?.USD) {
                    const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
                    calculatedPrice = calcResponse.data.lcb_c_code_usd / usdToEurRate;
                  } else if (formData.currency === 'TRY') {
                    calculatedPrice = calcResponse.data.lcb_c_code_try;
                  }
                  priceCalculated = true;
                }
              } catch (error) {
                console.error('LÇB C Kodu auto-calc error:', error);
              }
            } else {
              const defaultPrice = serviceItem.default_price || 0;
              if (defaultPrice > 0) {
                calculatedPrice = defaultPrice;
                priceCalculated = true;
              } else {
                toast.warning(`LÇB C Kodu için gemi Net Tonaj (NT) bilgisi gerekli. Lütfen fiyatı manuel girin.`);
              }
            }
          }
          // Demirleme Ücreti için özel hesaplama (Liman Başkanlığı - GT bazlı)
          else if (settings?.port_authority_anchoring_service_name && serviceName === settings.port_authority_anchoring_service_name) {
            if (selectedVessel && selectedVessel.gross_tonnage) {
              try {
                const flag = (selectedVessel.flag || '').toLowerCase();
                const isTurkishFlag = flag.includes('turk') || flag.includes('turkey');
                
                const calcResponse = await api.post(`/port-authority/calculate-anchoring?gross_tonnage=${parseFloat(selectedVessel.gross_tonnage)}&days=1&is_turkish_flag=${isTurkishFlag}`);
                
                if (calcResponse.data) {
                  if (formData.currency === 'USD') {
                    calculatedPrice = calcResponse.data.total_fee_usd;
                  } else if (formData.currency === 'EUR' && exchangeRates?.currencies?.EUR && exchangeRates?.currencies?.USD) {
                    const usdToEurRate = exchangeRates.currencies.USD.forex_buying / exchangeRates.currencies.EUR.forex_buying;
                    calculatedPrice = calcResponse.data.total_fee_usd / usdToEurRate;
                  } else if (formData.currency === 'TRY') {
                    calculatedPrice = calcResponse.data.total_fee_try;
                  }
                  priceCalculated = true;
                }
              } catch (error) {
                console.error('Demirleme auto-calc error:', error);
              }
            } else {
              const defaultPrice = serviceItem.default_price || 0;
              if (defaultPrice > 0) {
                calculatedPrice = defaultPrice;
                priceCalculated = true;
              } else {
                toast.warning(`Demirleme için gemi Gross Tonaj (GT) bilgisi gerekli. Lütfen fiyatı manuel girin.`);
              }
            }
          }
          
          // Eğer özel hesaplama yapılmadıysa, son fiyatı API'den çek
          if (!priceCalculated) {
            try {
              const response = await api.get('/service-prices/last-price', {
                params: {
                  service_name: serviceName,
                  client_id: formData.client_id || null
                }
              });
              
              calculatedPrice = response.data?.unit_price || 0;
            } catch (error) {
              calculatedPrice = 0;
            }
          }
          
          newLines.push({
            description: serviceName,
            quantity: 1,
            unit_price: parseFloat(calculatedPrice.toFixed(2)),
            total: parseFloat(calculatedPrice.toFixed(2))
          });
        }
        
        setFormData(prev => ({ ...prev, operation_type: operationType, lines: newLines }));
        // Operasyon tipinin Türkçe adını bul
        const selectedType = portCallTypes.find(t => t.id === operationType);
        const operationName = selectedType ? selectedType.name_tr : operationType;
        toast.success(`${servicesToAdd.length} ${operationName} hizmet kalemi otomatik eklendi`);
      } else {
        // No services to add, just update operation type
        setFormData({ ...formData, operation_type: operationType, lines: [{ description: '', quantity: 1, unit: 'ADET', unit_price: 0, has_vat: false, vat_rate: 20, total: 0 }] });
      }
    } else {
      // No operation type selected, reset
      setFormData({ ...formData, operation_type: operationType, lines: [{ description: '', quantity: 1, unit_price: 0, total: 0 }] });
    }
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalVat = 0;
    
    formData.lines.forEach(line => {
      const baseTotal = line.quantity * line.unit_price;
      const vatAmount = line.has_vat ? baseTotal * (line.vat_rate || 20) / 100 : 0;
      subtotal += baseTotal;
      totalVat += vatAmount;
    });
    
    const total = subtotal + totalVat;
    return { subtotal, totalVat, total };
  };

  // Proforma Yazdırma Fonksiyonu - PrintUtils ile entegre
  const printProforma = (proforma) => {
    // Gemi bilgilerini bul
    const selectedVessel = vessels.find(v => v.name === proforma.vessel_name);
    const vesselImo = selectedVessel?.imo || '-';
    const vesselGrt = selectedVessel?.gross_tonnage || '-';
    const vesselNrt = selectedVessel?.net_tonnage || '-';
    
    // Port call bilgilerini bul (ETA/ETD için)
    const relatedPortCall = portCalls.find(pc => pc.id === proforma.port_call_id);
    const etaFormatted = relatedPortCall?.eta ? new Date(relatedPortCall.eta).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
    const etdFormatted = relatedPortCall?.etd ? new Date(relatedPortCall.etd).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
    
    // Build lines HTML
    const linesHtml = (proforma.lines || []).map(line => {
      const vatText = line.has_vat ? '%' + (line.vat_rate || 20) : '-';
      // Birim fiyatı 3 ondalık, toplam 2 ondalık göster
      const unitPriceFormatted = (line.unit_price || 0).toFixed(3).replace(/\.?0+$/, '') || '0';
      return '<tr>' +
        '<td>' + line.description + '</td>' +
        '<td>' + line.quantity + '</td>' +
        '<td>' + (line.unit || 'ADET') + '</td>' +
        '<td>' + proforma.currency + ' ' + unitPriceFormatted + '</td>' +
        '<td>' + vatText + '</td>' +
        '<td style="text-align:right"><strong>' + proforma.currency + ' ' + (line.total || 0).toFixed(2) + '</strong></td>' +
        '</tr>';
    }).join('');
    
    const vatSection = (proforma.total_vat || 0) > 0 ? 
      '<div class="total-row" style="color:#059669"><span>KDV / VAT:</span><span>' + proforma.currency + ' ' + (proforma.total_vat || 0).toFixed(2) + '</span></div>' : '';
    
    const notesSection = proforma.notes ? 
      '<div style="margin-top:20px; padding:10px; background:#f0fdf4; border-radius:6px; border:1px solid #86efac"><strong>Notlar / Notes:</strong><br/>' + proforma.notes + '</div>' : '';

    // REVISED badge if revised
    const revisedBadge = proforma.is_revised ? 
      '<div style="position: absolute; top: 80px; right: 25px; background: #dc2626; color: white; padding: 8px 20px; font-size: 14px; font-weight: bold; letter-spacing: 2px; border-radius: 4px; transform: rotate(-5deg); box-shadow: 0 2px 8px rgba(220, 38, 38, 0.4);">REVISED</div>' : '';

    const content = `
      <style>
        @page { size: A4; margin: 15mm; } * { box-sizing: border-box; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 25px; padding-bottom: 15px; border-bottom: 3px solid #059669; }
        .company-name { font-size: 22px; font-weight: bold; color: #059669; }
        .company-subtitle { font-size: 10px; color: #64748b; }
        .proforma-info { text-align: right; }
        .proforma-number { font-size: 16px; font-weight: bold; color: #059669; }
        .proforma-date { font-size: 11px; color: #64748b; }
        .title { font-size: 18px; text-align: center; background: linear-gradient(135deg, #059669, #10b981); color: white; padding: 10px; border-radius: 8px; margin: 20px 0; text-transform: uppercase; letter-spacing: 2px; }
        .info-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 20px; }
        .info-box { background: #f0fdf4; border: 1px solid #86efac; border-radius: 6px; padding: 8px 10px; }
        .info-label { font-size: 8px; text-transform: uppercase; color: #64748b; font-weight: 600; margin-bottom: 2px; }
        .info-value { font-size: 11px; font-weight: 600; }
        table { width: 100%; border-collapse: collapse; margin: 15px 0; }
        th { background: #059669; color: white; padding: 8px 10px; text-align: left; font-size: 10px; text-transform: uppercase; }
        td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; }
        tr:nth-child(even) { background: #f0fdf4; }
        .totals { background: #f0fdf4; border-radius: 8px; padding: 15px; margin-top: 20px; border: 1px solid #86efac; }
        .total-row { display: flex; justify-content: space-between; padding: 5px 0; }
        .grand-total { font-size: 18px; font-weight: bold; color: #059669; border-top: 2px solid #059669; padding-top: 10px; margin-top: 10px; }
        .footer { margin-top: 40px; text-align: center; font-size: 9px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 10px; }
        .watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); font-size: 80px; color: rgba(5, 150, 105, 0.08); font-weight: bold; z-index: -1; }
      </style>
      <div style="position: relative;">
        ${revisedBadge}
        <div class="watermark">PROFORMA</div>
        <div class="header">
          <div>
            <div class="company-name">ROTA MARITIME AGENCY</div>
            <div class="company-subtitle">Ship Agency Services - Sinop, Turkey</div>
          </div>
          <div class="proforma-info">
            <div class="proforma-number">${proforma.number}</div>
            <div class="proforma-date">${new Date(proforma.created_at).toLocaleDateString('tr-TR')}</div>
          </div>
        </div>
        <div class="title">PROFORMA / QUOTATION</div>
        <div class="info-grid">
          <div class="info-box"><div class="info-label">Müşteri / Client</div><div class="info-value">${proforma.client_name}</div></div>
          <div class="info-box"><div class="info-label">Gemi / Vessel</div><div class="info-value">${proforma.vessel_name || '-'}</div></div>
          <div class="info-box"><div class="info-label">IMO</div><div class="info-value">${vesselImo}</div></div>
          <div class="info-box"><div class="info-label">GRT / NRT</div><div class="info-value">${vesselGrt} / ${vesselNrt}</div></div>
          <div class="info-box"><div class="info-label">Liman / Port</div><div class="info-value">${proforma.port || '-'}</div></div>
          <div class="info-box"><div class="info-label">Operasyon / Operation</div><div class="info-value">${proforma.operation_type || '-'}</div></div>
          <div class="info-box"><div class="info-label">Geliş / ETA</div><div class="info-value">${etaFormatted}</div></div>
          <div class="info-box"><div class="info-label">Gidiş / ETD</div><div class="info-value">${etdFormatted}</div></div>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width:40%">Açıklama / Description</th>
              <th>Miktar / Qty</th>
              <th>Birim / Unit</th>
              <th>B.Fiyat / Price</th>
              <th>KDV / VAT</th>
              <th style="text-align:right">Toplam / Total</th>
            </tr>
          </thead>
          <tbody>${linesHtml}</tbody>
        </table>
        <div class="totals">
          <div class="total-row"><span>Ara Toplam / Subtotal:</span><span>${proforma.currency} ${(proforma.subtotal || 0).toFixed(2)}</span></div>
          ${vatSection}
          <div class="total-row grand-total"><span>GENEL TOPLAM / GRAND TOTAL:</span><span>${proforma.currency} ${(proforma.total || 0).toFixed(2)}</span></div>
        </div>
        ${notesSection}
        <div class="footer">Rota Maritime Agency - Sinop, Turkey - ${new Date().toLocaleString('tr-TR')}</div>
      </div>
    `;
    
    openPrintWindow(content, `Proforma - ${proforma.number}`);
    toast.success('Proforma yazdırma penceresi açıldı');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedProforma) {
        await api.put(`/proformas/${selectedProforma.id}`, formData);
        toast.success('Proforma başarıyla güncellendi');
      } else {
        await api.post('/proformas', formData);
        toast.success('Proforma başarıyla oluşturuldu');
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (selectedProforma ? 'Proforma güncellenemedi' : 'Proforma oluşturulamadı'));
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      vessel_name: '',
      port: '',
      operation_type: '',
      status: 'draft',
      currency: 'USD',
      notes: '',
      is_revised: false,
      lines: [{ description: '', quantity: 1, unit: 'ADET', unit_price: 0, has_vat: false, vat_rate: 20, total: 0 }]
    });
    setSelectedProforma(null);
    setVesselSearchTerm(null);
    setPortSearchTerm(null);
    setShowVesselDropdown(false);
    setShowPortDropdown(false);
  };

  const handleViewDetail = (proforma) => {
    setSelectedProforma(proforma);
    setShowDetail(true);
  };

  const handleEdit = (proforma) => {
    setSelectedProforma(proforma);
    setPortSearchTerm(null); // Reset port search term to show saved port value
    setFormData({
      client_id: proforma.client_id,
      vessel_name: proforma.vessel_name || '',
      port: proforma.port || '',
      operation_type: proforma.operation_type || '',
      status: proforma.status || 'draft',
      currency: proforma.currency || 'USD',
      notes: proforma.notes || '',
      is_revised: proforma.is_revised || false,
      lines: (proforma.lines || []).map(line => ({
        ...line,
        unit: line.unit || 'ADET',
        has_vat: line.has_vat || false,
        vat_rate: line.vat_rate || 20
      }))
    });
    setShowModal(true);
  };

  const handleDelete = async (proformaId, proformaNumber) => {
    if (!window.confirm(`"${proformaNumber}" proformasını silmek istediğinizden emin misiniz?`)) return;
    
    try {
      await api.delete(`/proformas/${proformaId}`);
      toast.success('Proforma başarıyla silindi');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Proforma silinemedi');
    }
  };

  const handleUpdateStatus = async (proformaId, newStatus) => {
    // Proforma onaylama artık direkt yapılıyor - Port Call seçimi gerekmiyor
    // Port Call oluşturulduğunda zaten onaylı proforma seçiliyor ve bağlanıyor
    await performStatusUpdate(proformaId, newStatus);
  };

  const performStatusUpdate = async (proformaId, newStatus) => {
    try {
      // Backend'i güncelle
      const response = await api.put(`/proformas/${proformaId}/status?status=${newStatus}`);
      
      // Tüm proformaları yeniden yükle
      const proformasRes = await api.get('/proformas');
      setProformas(proformasRes.data);
      
      // Modal içinde gösterilen proformayı güncelle
      const updatedProforma = proformasRes.data.find(p => p.id === proformaId);
      if (updatedProforma) {
        setSelectedProforma(updatedProforma);
      }
      
      // Eğer Port Call otomatik oluşturulduysa bildir
      if (response.data.port_call_created) {
        toast.success(
          <div>
            <strong>Proforma onaylandı!</strong>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
              Liman Çağrısı <strong>{response.data.port_call_number}</strong> otomatik oluşturuldu.
            </p>
            <button 
              onClick={() => window.location.href = '/port-calls'}
              style={{ 
                marginTop: '0.5rem', 
                padding: '0.25rem 0.75rem', 
                background: '#3b82f6', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.75rem'
              }}
            >
              Liman Çağrılarına Git →
            </button>
          </div>,
          { duration: 8000 }
        );
      } else {
        toast.success('Durum başarıyla güncellendi');
      }
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Durum güncellenemedi');
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      draft: 'badge-draft',
      sent: 'badge-sent',
      approved: 'badge-approved',
      converted: 'badge-converted',
      cancelled: 'badge-cancelled'
    };
    return classes[status] || 'badge-draft';
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Taslak',
      sent: 'Gönderildi',
      approved: 'Onaylandı',
      converted: 'Dönüştürüldü',
      cancelled: 'İptal Edildi'
    };
    return labels[status] || status;
  };

  const { subtotal, totalVat, total } = calculateTotals();

  // Proformaları duruma göre grupla
  const groupedProformas = {
    draft: proformas.filter(p => p.status === 'draft'),
    sent: proformas.filter(p => p.status === 'sent'),
    approved: proformas.filter(p => p.status === 'approved'),
    converted: proformas.filter(p => p.status === 'converted'),
    cancelled: proformas.filter(p => p.status === 'cancelled')
  };

  // Proforma tablosu render fonksiyonu
  const renderProformaTable = (proformaList, title, bgColor, textColor) => {
    if (proformaList.length === 0) return null;
    
    return (
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: '0.75rem 1rem', 
          backgroundColor: bgColor, 
          color: textColor,
          borderRadius: '8px 8px 0 0',
          fontWeight: '600'
        }}>
          <span>{title}</span>
          <span style={{ 
            backgroundColor: 'rgba(255,255,255,0.2)', 
            padding: '0.25rem 0.75rem', 
            borderRadius: '12px',
            fontSize: '0.875rem'
          }}>
            {proformaList.length}
          </span>
        </div>
        <div className="table-container" style={{ borderRadius: '0 0 8px 8px', border: `1px solid ${bgColor}`, borderTop: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>Numara</th>
                <th>Müşteri</th>
                <th>Gemi</th>
                <th>Liman</th>
                <th>Toplam</th>
                <th>Oluşturma</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {proformaList.map((proforma) => (
                <tr key={proforma.id} data-testid={`proforma-row-${proforma.id}`}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <strong>{proforma.number}</strong>
                      {proforma.is_revised && (
                        <span style={{
                          background: '#dc2626',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '10px',
                          fontWeight: 'bold'
                        }}>
                          REVİZE
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{proforma.client_name}</td>
                  <td>{proforma.vessel_name || '-'}</td>
                  <td>{proforma.port || '-'}</td>
                  <td>
                    <strong>
                      {proforma.currency} {proforma.total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </strong>
                  </td>
                  <td>{new Date(proforma.created_at).toLocaleDateString('tr-TR')}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button 
                        className="action-btn" 
                        onClick={() => handleViewDetail(proforma)}
                        title="Detayları Görüntüle"
                        data-testid={`view-proforma-${proforma.id}`}
                      >
                        <Eye size={18} />
                      </button>
                      {proforma.status !== 'converted' && proforma.status !== 'cancelled' && (
                        <button 
                          className="action-btn" 
                          onClick={() => handleEdit(proforma)}
                          title="Düzenle"
                          data-testid={`edit-proforma-${proforma.id}`}
                        >
                          <Edit2 size={18} />
                        </button>
                      )}
                      <button 
                        className="action-btn" 
                        onClick={() => printProforma(proforma)}
                        title="Yazdır"
                        data-testid={`print-proforma-${proforma.id}`}
                        style={{ backgroundColor: '#059669', color: 'white' }}
                      >
                        <Printer size={18} />
                      </button>
                      {proforma.status !== 'converted' && (
                        <button 
                          className="action-btn action-btn-danger" 
                          onClick={() => handleDelete(proforma.id, proforma.number)}
                          title="Sil"
                          data-testid={`delete-proforma-${proforma.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="content">
      <div className="topbar">
        <h1 className="topbar-title" data-testid="page-title">Proformalar</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => { resetForm(); setShowModal(true); }}
          data-testid="create-proforma-btn"
        >
          <Plus size={18} />
          Proforma Oluştur
        </button>
      </div>
      
      <div style={{ padding: '2rem' }}>
        {loading ? (
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '2rem' }}>
              Yükleniyor...
            </div>
          </div>
        ) : proformas.length === 0 ? (
          <div className="card">
            <div className="card-body">
              <div className="empty-state">
                <div className="empty-state-icon"><FileText /></div>
                <h3 className="empty-state-title">Henüz proforma yok</h3>
                <p className="empty-state-text">İlk proforma faturanızı oluşturun</p>
                <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
                  <Plus size={18} />
                  Proforma Oluştur
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Özet Kartları */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(5, 1fr)', 
              gap: '1rem', 
              marginBottom: '2rem' 
            }}>
              <div style={{ 
                background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', 
                padding: '1rem', 
                borderRadius: '8px', 
                color: 'white',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{groupedProformas.draft.length}</div>
                <div style={{ opacity: 0.9 }}>Taslak</div>
              </div>
              <div style={{ 
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)', 
                padding: '1rem', 
                borderRadius: '8px', 
                color: 'white',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{groupedProformas.sent.length}</div>
                <div style={{ opacity: 0.9 }}>Gönderildi</div>
              </div>
              <div style={{ 
                background: 'linear-gradient(135deg, #10b981, #059669)', 
                padding: '1rem', 
                borderRadius: '8px', 
                color: 'white',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{groupedProformas.approved.length}</div>
                <div style={{ opacity: 0.9 }}>Onaylandı</div>
              </div>
              <div style={{ 
                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', 
                padding: '1rem', 
                borderRadius: '8px', 
                color: 'white',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{groupedProformas.converted.length}</div>
                <div style={{ opacity: 0.9 }}>Faturaya Dönüştürüldü</div>
              </div>
              <div style={{ 
                background: 'linear-gradient(135deg, #ef4444, #dc2626)', 
                padding: '1rem', 
                borderRadius: '8px', 
                color: 'white',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{groupedProformas.cancelled.length}</div>
                <div style={{ opacity: 0.9 }}>İptal Edildi</div>
              </div>
            </div>

            {/* Duruma Göre Gruplandırılmış Tablolar */}
            {renderProformaTable(groupedProformas.draft, '📝 Taslak Proformalar', '#fbbf24', 'white')}
            {renderProformaTable(groupedProformas.sent, '📤 Gönderilen Proformalar', '#3b82f6', 'white')}
            {renderProformaTable(groupedProformas.approved, '✅ Onaylanan Proformalar', '#10b981', 'white')}
            {renderProformaTable(groupedProformas.converted, '🔄 Faturaya Dönüştürülen Proformalar', '#8b5cf6', 'white')}
            {renderProformaTable(groupedProformas.cancelled, '❌ İptal Edilen Proformalar', '#ef4444', 'white')}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h2 className="modal-title" data-testid="modal-title">
                {selectedProforma ? 'Proforma Düzenle' : 'Proforma Fatura Oluştur'}
              </h2>
              <button className="modal-close" onClick={() => { setShowModal(false); resetForm(); }}>×</button>
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                {/* Döviz Kurları Widget */}
                {exchangeRates && exchangeRates.currencies && exchangeRates.currencies.USD && exchangeRates.currencies.EUR && (
                  <div style={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    marginBottom: '1.5rem',
                    color: 'white'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', fontSize: '0.875rem' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ opacity: 0.9, marginBottom: '0.25rem' }}>USD Alış</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                          {exchangeRates.currencies.USD.forex_buying.toFixed(4)} ₺
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ opacity: 0.9, marginBottom: '0.25rem' }}>EUR Alış</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                          {exchangeRates.currencies.EUR.forex_buying.toFixed(4)} ₺
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ opacity: 0.9, marginBottom: '0.25rem' }}>Parite (EUR/USD)</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                          {(exchangeRates.currencies.EUR.forex_buying / exchangeRates.currencies.USD.forex_buying).toFixed(4)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Müşteri *</label>
                    <select
                      className="form-select"
                      value={formData.client_id}
                      onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                      required
                      data-testid="client-select"
                    >
                      <option value="">Müşteri Seçin</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>{client.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Gemi Adı</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Gemi ara veya seç..."
                        value={formData.vessel_name}
                        onChange={async (e) => {
                          const newVesselName = e.target.value;
                          setFormData({ ...formData, vessel_name: newVesselName });
                          setShowVesselDropdown(true);
                          
                          // Gemi adı tamamen silindiğinde fiyatları sıfırla
                          if (!newVesselName) {
                            const updatedLines = formData.lines.map(line => {
                              // Özel hesaplama gerektiren hizmetlerin fiyatını sıfırla
                              if (line.description.includes('HSSGM') || 
                                  line.description.includes('Light Dues') || 
                                  line.description.includes('DTO')) {
                                return { ...line, unit_price: 0, total: 0 };
                              }
                              return line;
                            });
                            setFormData(prev => ({ ...prev, lines: updatedLines }));
                          }
                        }}
                        onFocus={() => setShowVesselDropdown(true)}
                        onBlur={() => setTimeout(() => setShowVesselDropdown(false), 200)}
                        data-testid="vessel-input"
                      />
                      {showVesselDropdown && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          maxHeight: '200px',
                          overflowY: 'auto',
                          background: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          marginTop: '4px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          zIndex: 1000
                        }}>
                          {vessels
                            .filter(vessel => 
                              vessel.name.toLowerCase().includes((formData.vessel_name || '').toLowerCase()) ||
                              (vessel.imo && vessel.imo.toLowerCase().includes((formData.vessel_name || '').toLowerCase()))
                            )
                            .slice(0, 10)
                            .map(vessel => (
                              <div
                                key={vessel.id}
                                onClick={async () => {
                                  const newVesselName = vessel.name;
                                  setFormData({ ...formData, vessel_name: newVesselName });
                                  setShowVesselDropdown(false);
                                  
                                  // Gemi seçildiğinde, mevcut hizmet kalemlerinin fiyatlarını yeniden hesapla
                                  const selectedVessel = vessels.find(v => v.name === newVesselName);
                                  if (selectedVessel && formData.lines.length > 0) {
                                    const updatedLines = await Promise.all(formData.lines.map(async (line) => {
                                      let newPrice = line.unit_price;
                                      
                                      // HSSGM için yeniden hesapla
                                      if (line.description === 'HSSGM Tahakuku / Sanitary Dues' && selectedVessel.net_tonnage && settings && exchangeRates) {
                                        const tariffTRY = settings.hssgm_commercial || 17.27;
                                        const nrt = parseFloat(selectedVessel.net_tonnage);
                                        const totalTRY = nrt * tariffTRY;
                                        
                                        if (formData.currency === 'USD' && exchangeRates.currencies.USD) {
                                          newPrice = totalTRY / exchangeRates.currencies.USD.forex_buying;
                                        } else if (formData.currency === 'EUR' && exchangeRates.currencies.EUR) {
                                          newPrice = totalTRY / exchangeRates.currencies.EUR.forex_buying;
                                        } else if (formData.currency === 'TRY') {
                                          newPrice = totalTRY;
                                        }
                                      }
                                      // Kıyı Emniyeti için yeniden hesapla
                                      else if (line.description === 'Kıyı Emniyeti Fener Ücretti / Light Dues' && selectedVessel.net_tonnage && selectedVessel.flag && settings) {
                                        const nrt = parseFloat(selectedVessel.net_tonnage);
                                        const flag = selectedVessel.flag.toLowerCase();
                                        
                                        let rate0_800, rate800_plus;
                                        if (flag.includes('turk') || flag.includes('turkey')) {
                                          rate0_800 = settings.light_dues_turkish_0_800 || 0.1241856;
                                          rate800_plus = settings.light_dues_turkish_800_plus || 0.0620928;
                                        } else {
                                          rate0_800 = settings.light_dues_foreign_commercial_0_800 || 0.22176;
                                          rate800_plus = settings.light_dues_foreign_commercial_800_plus || 0.11088;
                                        }
                                        
                                        let singleDirectionUSD = 0;
                                        if (nrt <= 800) {
                                          singleDirectionUSD = nrt * rate0_800;
                                        } else {
                                          singleDirectionUSD = (800 * rate0_800) + ((nrt - 800) * rate800_plus);
                                        }
                                        newPrice = singleDirectionUSD * 2;
                                      }
                                      // DTO Liman Hizmet için yeniden hesapla
                                      else if ((line.description === 'DTO Liman hizmet Ücreti/ DTO Port Service Fee' || line.description === 'DTO Liman hizmet Ücreti / DTO Port Service Fee') && selectedVessel.flag && settings && exchangeRates) {
                                        const flag = selectedVessel.flag.toLowerCase();
                                        let feeTRY = flag.includes('turk') || flag.includes('turkey') ? 
                                          (settings.dto_port_service_turkish || 2800.0) : 
                                          (settings.dto_port_service_foreign || 4900.0);
                                        
                                        if (formData.currency === 'USD' && exchangeRates.currencies.USD) {
                                          newPrice = feeTRY / exchangeRates.currencies.USD.forex_buying;
                                        } else if (formData.currency === 'EUR' && exchangeRates.currencies.EUR) {
                                          newPrice = feeTRY / exchangeRates.currencies.EUR.forex_buying;
                                        } else if (formData.currency === 'TRY') {
                                          newPrice = feeTRY;
                                        }
                                      }
                                      
                                      return {
                                        ...line,
                                        unit_price: parseFloat(newPrice.toFixed(2)),
                                        total: parseFloat((line.quantity * newPrice).toFixed(2))
                                      };
                                    }));
                                    
                                    setFormData(prev => ({ ...prev, vessel_name: newVesselName, lines: updatedLines }));
                                  }
                                }}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  fontSize: '0.875rem'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.background = 'white'}
                              >
                                <div style={{ fontWeight: 600 }}>{vessel.name}</div>
                                {vessel.imo && (
                                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                    IMO: {vessel.imo} • {vessel.flag || 'N/A'}
                                  </div>
                                )}
                              </div>
                            ))}
                          {vessels.filter(vessel => 
                            vessel.name.toLowerCase().includes((formData.vessel_name || '').toLowerCase()) ||
                            (vessel.imo && vessel.imo.toLowerCase().includes((formData.vessel_name || '').toLowerCase()))
                          ).length === 0 && (
                            <div style={{ padding: '12px', textAlign: 'center', color: '#64748b', fontSize: '0.875rem' }}>
                              Gemi bulunamadı
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Liman Çağrısı Tipi</label>
                    <select
                      className="form-select"
                      value={formData.operation_type}
                      onChange={(e) => handleOperationTypeChange(e.target.value)}
                      data-testid="operation-type-select"
                    >
                      <option value="">Seçin</option>
                      {portCallTypes.map((type, idx) => (
                        <option key={idx} value={type.id}>
                          {type.name_tr} / {type.name_en}
                        </option>
                      ))}
                      <option value="other">Diğer / Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Liman</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        className="form-input"
                        value={portSearchTerm !== null ? portSearchTerm : formData.port || ''}
                        onChange={(e) => {
                          setPortSearchTerm(e.target.value);
                          setShowPortDropdown(true);
                        }}
                        onFocus={() => {
                          setPortSearchTerm(formData.port || '');
                          setShowPortDropdown(true);
                        }}
                        onBlur={() => setTimeout(() => setShowPortDropdown(false), 200)}
                        placeholder="Liman kodu veya adı yazın..."
                        data-testid="port-select"
                      />
                      {showPortDropdown && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          maxHeight: '250px',
                          overflowY: 'auto',
                          background: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          zIndex: 1000
                        }}>
                          {ports
                            .filter(p => {
                              const search = (portSearchTerm || '').toLowerCase();
                              return search === '' || 
                                p.code.toLowerCase().includes(search) || 
                                p.name.toLowerCase().includes(search);
                            })
                            .slice(0, 50)
                            .map(port => (
                              <div
                                key={port.code}
                                onClick={() => {
                                  setFormData({ ...formData, port: `${port.name} (${port.code})`, port_code: port.code });
                                  setPortSearchTerm(null);
                                  setShowPortDropdown(false);
                                }}
                                style={{
                                  padding: '0.5rem 1rem',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  background: formData.port_code === port.code ? '#eff6ff' : 'transparent'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.background = formData.port_code === port.code ? '#eff6ff' : 'transparent'}
                              >
                                <span style={{ fontWeight: 600, color: port.code.startsWith('TR') ? '#16a34a' : '#3b82f6' }}>{port.code}</span>
                                <span style={{ marginLeft: '0.5rem', color: '#64748b' }}>{port.name}</span>
                              </div>
                            ))}
                          {ports.filter(p => {
                            const search = (portSearchTerm || '').toLowerCase();
                            return search === '' || p.code.toLowerCase().includes(search) || p.name.toLowerCase().includes(search);
                          }).length === 0 && (
                            <div style={{ padding: '1rem', textAlign: 'center', color: '#9ca3af' }}>
                              Liman bulunamadı
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Durum</label>
                    <select
                      className="form-select"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      data-testid="status-select"
                    >
                      <option value="draft">Taslak</option>
                      <option value="sent">Gönderildi</option>
                      <option value="approved">Onaylandı</option>
                      <option value="cancelled">İptal Edildi</option>
                    </select>
                  </div>
                  
                  {/* Revize Checkbox - Sadece düzenleme modunda ve fiyat değişikliği yapıldığında */}
                  {selectedProforma && (
                    <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem', 
                        cursor: 'pointer',
                        padding: '0.5rem 1rem',
                        background: formData.is_revised ? '#fef2f2' : '#f8fafc',
                        border: formData.is_revised ? '2px solid #ef4444' : '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}>
                        <input
                          type="checkbox"
                          checked={formData.is_revised || false}
                          onChange={(e) => setFormData({ ...formData, is_revised: e.target.checked })}
                          style={{ width: '18px', height: '18px', accentColor: '#ef4444' }}
                        />
                        <span style={{ 
                          fontWeight: 600, 
                          color: formData.is_revised ? '#dc2626' : '#64748b' 
                        }}>
                          REVİZE EDİLDİ
                        </span>
                      </label>
                    </div>
                  )}
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Para Birimi</label>
                    <select
                      className="form-select"
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      data-testid="currency-select"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="TRY">TRY</option>
                    </select>
                  </div>
                </div>
                
                {/* Hizmet Kalemleri - Başlıklar ile */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <label className="form-label" style={{ marginBottom: 0, fontWeight: '600' }}>Hizmet Kalemleri *</label>
                    <button type="button" className="btn btn-sm btn-secondary" onClick={addLine} data-testid="add-line-btn">
                      <Plus size={16} /> Satır Ekle
                    </button>
                  </div>
                  
                  {/* Tablo Başlıkları - Sadece Desktop */}
                  <div className="desktop-only" style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '30px 2fr 70px 70px 90px 50px 70px 90px 70px', 
                    gap: '0.5rem', 
                    marginBottom: '0.5rem',
                    padding: '0.5rem',
                    backgroundColor: '#f1f5f9',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: '#475569'
                  }}>
                    <div></div>
                    <div>Hizmet</div>
                    <div>Adet</div>
                    <div>Birim</div>
                    <div>Birim Fiyat</div>
                    <div>KDV</div>
                    <div>KDV %</div>
                    <div>Toplam</div>
                    <div></div>
                  </div>
                  
                  <DndContext 
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => {
                      const { active, over } = event;
                      if (active.id !== over?.id) {
                        const oldIndex = formData.lines.findIndex((_, i) => `line-${i}` === active.id);
                        const newIndex = formData.lines.findIndex((_, i) => `line-${i}` === over.id);
                        setFormData(prev => ({
                          ...prev,
                          lines: arrayMove(prev.lines, oldIndex, newIndex)
                        }));
                      }
                    }}
                  >
                    <SortableContext items={formData.lines.map((_, i) => `line-${i}`)} strategy={verticalListSortingStrategy}>
                      {formData.lines.map((line, index) => (
                        <SortableLineItem
                          key={`line-${index}`}
                          id={`line-${index}`}
                          index={index}
                          line={line}
                          serviceItems={serviceItems}
                          serviceSearchTerms={serviceSearchTerms}
                          showServiceDropdown={showServiceDropdown}
                          setServiceSearchTerms={setServiceSearchTerms}
                          setShowServiceDropdown={setShowServiceDropdown}
                          handleLineChange={handleLineChange}
                          removeLine={removeLine}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
                
                <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span>Ara Toplam:</span>
                    <strong data-testid="subtotal">{formData.currency} {subtotal.toFixed(2)}</strong>
                  </div>
                  {totalVat > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', color: '#059669' }}>
                      <span>Toplam KDV:</span>
                      <strong data-testid="tax-amount">{formData.currency} {totalVat.toFixed(2)}</strong>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.25rem', fontWeight: 700 }}>
                    <span>Genel Toplam:</span>
                    <span className="text-primary" data-testid="total">{formData.currency} {total.toFixed(2)}</span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Notlar</label>
                  <textarea
                    className="form-input"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="3"
                    data-testid="notes-input"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" data-testid="save-proforma-btn">
                  {selectedProforma ? 'Proforma Güncelle' : 'Proforma Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && selectedProforma && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h2 className="modal-title" data-testid="detail-title" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                Proforma {selectedProforma.number}
                {selectedProforma.is_revised && (
                  <span style={{
                    background: '#dc2626',
                    color: 'white',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    REVISED
                  </span>
                )}
              </h2>
              <button className="modal-close" onClick={() => setShowDetail(false)}>×</button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Müşteri</div>
                  <div style={{ fontWeight: 600 }}>{selectedProforma.client_name}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Durum</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span className={`badge ${getStatusBadgeClass(selectedProforma.status)}`}>
                      {getStatusLabel(selectedProforma.status)}
                    </span>
                    {selectedProforma.is_revised && (
                      <span style={{
                        background: '#dc2626',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '10px',
                        fontWeight: 'bold'
                      }}>
                        REVISED
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Gemi</div>
                  <div>{selectedProforma.vessel_name || '-'}</div>
                  {(() => {
                    const v = vessels.find(v => v.name === selectedProforma.vessel_name);
                    if (v) {
                      return (
                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                          IMO: {v.imo || '-'} | GRT: {v.gross_tonnage || '-'} | NRT: {v.net_tonnage || '-'}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Liman</div>
                  <div>{selectedProforma.port || '-'}</div>
                </div>
                {/* Geliş/Gidiş Tarihleri */}
                {(() => {
                  const pc = portCalls.find(pc => pc.id === selectedProforma.port_call_id);
                  if (pc) {
                    return (
                      <>
                        <div>
                          <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Geliş (ETA)</div>
                          <div>{pc.eta ? new Date(pc.eta).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Gidiş (ETD)</div>
                          <div>{pc.etd ? new Date(pc.etd).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</div>
                        </div>
                      </>
                    );
                  }
                  return null;
                })()}
              </div>
              
              <h4 style={{ marginBottom: '1rem' }}>Hizmet Kalemleri</h4>
              <table style={{ marginBottom: '1.5rem' }}>
                <thead>
                  <tr>
                    <th>Açıklama</th>
                    <th>Miktar</th>
                    <th>Birim Fiyat</th>
                    <th>KDV</th>
                    <th>Toplam</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedProforma.lines.map((line, index) => (
                    <tr key={index}>
                      <td>{line.description}</td>
                      <td>{line.quantity} {line.unit || 'ADET'}</td>
                      <td>{selectedProforma.currency} {(line.unit_price || 0).toFixed(3).replace(/\.?0+$/, '') || '0'}</td>
                      <td>{line.has_vat ? `%${line.vat_rate}` : '-'}</td>
                      <td>{selectedProforma.currency} {line.total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Ara Toplam:</span>
                  <strong>{selectedProforma.currency} {selectedProforma.subtotal.toFixed(2)}</strong>
                </div>
                {(selectedProforma.total_vat || 0) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', color: '#059669' }}>
                    <span>Toplam KDV:</span>
                    <strong>{selectedProforma.currency} {(selectedProforma.total_vat || 0).toFixed(2)}</strong>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.25rem', fontWeight: 700 }}>
                  <span>Genel Toplam:</span>
                  <span className="text-primary">{selectedProforma.currency} {selectedProforma.total.toFixed(2)}</span>
                </div>
              </div>
              
              {selectedProforma.notes && (
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Notlar</div>
                  <div>{selectedProforma.notes}</div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {selectedProforma.status === 'draft' && (
                <button 
                  className="btn btn-primary" 
                  onClick={() => handleUpdateStatus(selectedProforma.id, 'sent')}
                  data-testid="mark-sent-btn"
                >
                  Gönderildi Olarak İşaretle
                </button>
              )}
              {selectedProforma.status === 'sent' && (
                <button 
                  className="btn btn-success" 
                  onClick={() => handleUpdateStatus(selectedProforma.id, 'approved')}
                  data-testid="mark-approved-btn"
                >
                  Onaylandı Olarak İşaretle
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Proformas;
